import { useCallback, useEffect, useState } from "react";
import API from './services/api';
import { getOrientationVideoSources } from './config/trainingMedia';
import whmisQuizQuestionsCsv from './data/WHMIS Quiz Questions.csv';

// ─── Design tokens ──────────────────────────────────────────────────────────
const T = "#1fa97a"; // teal primary
const TB = "#e8f7f2"; // teal bg
const TK = "#0f6e56"; // teal dark text
const GR = "var(--color-text-secondary)";
const CARD = {
  background:"var(--color-background-primary)",
  border:"0.5px solid var(--color-border-tertiary)",
  borderRadius:"var(--border-radius-lg)",
  marginBottom:"10px"
};
const inp = {
  width:"100%",boxSizing:"border-box",padding:"7px 10px",fontSize:"13px",
  border:"0.5px solid var(--color-border-secondary)",borderRadius:"var(--border-radius-md)",
  background:"var(--color-background-primary)",color:"var(--color-text-primary)",outline:"none",
  fontFamily:"inherit"
};
const lbl = { fontSize:"12px",color:GR,marginBottom:"3px",display:"block" };
const sel = {...inp};
const ta  = {...inp,resize:"vertical",minHeight:"72px"};
const btn = (bg="#fff",fg="var(--color-text-primary)",border=true) => ({
  padding:"7px 16px",fontSize:"13px",fontWeight:"500",cursor:"pointer",borderRadius:"var(--border-radius-md)",
  background:bg,color:fg,border: border ? `0.5px solid var(--color-border-secondary)` : "none",whiteSpace:"nowrap"
});
const grid2 = {display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"};
const grid3 = {display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"12px"};
const sec = {fontWeight:"500",fontSize:"15px",color:"var(--color-text-primary)",marginBottom:"10px"};
const orientationVideoStyle = {width:"100%",maxHeight:"260px",background:"#1e293b",borderRadius:"var(--border-radius-md)",display:"block",marginBottom:"10px"};
const orientationVideoSources = getOrientationVideoSources();
const checkboxRow = {
  display:"grid",
  gridTemplateColumns:"18px minmax(0, 1fr)",
  alignItems:"flex-start",
  justifyContent:"flex-start",
  justifyItems:"start",
  columnGap:"8px",
  width:"100%",
  maxWidth:"100%",
  boxSizing:"border-box",
  margin:0,
  padding:0,
  textAlign:"left",
  lineHeight:1.4,
  whiteSpace:"normal"
};
const checkboxInput = {accentColor:T,width:"14px",height:"14px",margin:"2px 0 0 0",padding:0,justifySelf:"start",alignSelf:"start",flex:"0 0 auto"};
const checkboxText = {display:"block",minWidth:0,maxWidth:"100%",whiteSpace:"normal",wordBreak:"normal",overflowWrap:"break-word",lineHeight:1.4,textAlign:"left"};
const Badge = ({c,t}) => (
  <span style={{fontSize:"11px",fontWeight:"500",padding:"2px 8px",borderRadius:"20px",
    background: c==="teal"?TB:c==="amber"?"#fffbeb":c==="blue"?"#eff6ff":"#f1f5f9",
    color: c==="teal"?TK:c==="amber"?"#92400e":c==="blue"?"#1d4ed8":"#475569",
    border:`0.5px solid ${c==="teal"?T:c==="amber"?"#fde68a":c==="blue"?"#bfdbfe":"#cbd5e1"}`}}>{t}</span>
);
const Divider = () => <div style={{height:"0.5px",background:"var(--color-border-tertiary)",margin:"12px 0"}} />;
const TRAINING_AUTOSAVE_PREFIX = 'diamondvalley.trainingModule.autosave.';
const TRAINING_AUTOSAVE_DELAY_MS = 600;
const TRAINING_AUTOSAVE_INTERVAL_MS = 15000;
const getTrainingAutosaveStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (error) {
    return null;
  }
};
const formatAutosaveStatus = (savedAt) => {
  const parsed = new Date(savedAt);
  if (Number.isNaN(parsed.getTime())) return 'Autosaved';
  return `Autosaved ${parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};
const loadTrainingAutosaveDraft = (draftKey) => {
  const storage = getTrainingAutosaveStorage();
  if (!storage) return null;

  try {
    const rawDraft = storage.getItem(`${TRAINING_AUTOSAVE_PREFIX}${draftKey}`);
    return rawDraft ? JSON.parse(rawDraft) : null;
  } catch (error) {
    return null;
  }
};
const saveTrainingAutosaveDraft = (draftKey, data) => {
  const storage = getTrainingAutosaveStorage();
  if (!storage) return '';

  try {
    const savedAt = new Date().toISOString();
    storage.setItem(`${TRAINING_AUTOSAVE_PREFIX}${draftKey}`, JSON.stringify({ savedAt, data }));
    return savedAt;
  } catch (error) {
    return '';
  }
};
const useTrainingAutosave = (draftKey, getSnapshot, restoreSnapshot) => {
  const [autosaveStatus, setAutosaveStatus] = useState('');

  useEffect(() => {
    const savedDraft = loadTrainingAutosaveDraft(draftKey);
    if (savedDraft?.data) {
      restoreSnapshot(savedDraft.data);
    }
    if (savedDraft?.savedAt) {
      setAutosaveStatus(formatAutosaveStatus(savedDraft.savedAt));
    }
  }, [draftKey, restoreSnapshot]);

  const saveSnapshot = useCallback(() => {
    const savedAt = saveTrainingAutosaveDraft(draftKey, getSnapshot());
    if (savedAt) {
      setAutosaveStatus(formatAutosaveStatus(savedAt));
    }
  }, [draftKey, getSnapshot]);

  useEffect(() => {
    const saveTimer = setTimeout(saveSnapshot, TRAINING_AUTOSAVE_DELAY_MS);
    return () => clearTimeout(saveTimer);
  }, [saveSnapshot]);

  useEffect(() => {
    const saveInterval = setInterval(saveSnapshot, TRAINING_AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(saveInterval);
  }, [saveSnapshot]);

  return autosaveStatus;
};

// ─── Course Category Data ────────────────────────────────────────────────────
const FALLBACK_TRAINING_CATEGORIES = [
  { CODE: 'first_aid_intermediate_cpr', NAME: 'First Aid - Intermediate with CPR', RECORD_TYPE: 'MANDATORY', SUMMARY_SECTION: 'Mandatory Training', TOOLBOX_ELIGIBLE: false },
  { CODE: 'whmis_2015', NAME: 'WHMIS 2015', RECORD_TYPE: 'MANDATORY', SUMMARY_SECTION: 'Mandatory Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'employee_orientation', NAME: 'Employee Orientation', RECORD_TYPE: 'MANDATORY', SUMMARY_SECTION: 'Mandatory Training', TOOLBOX_ELIGIBLE: false },
  { CODE: 'emergency_response_drills', NAME: 'Emergency Response Drills', RECORD_TYPE: 'MANDATORY', SUMMARY_SECTION: 'Mandatory Training', TOOLBOX_ELIGIBLE: false },
  { CODE: 'commercial_vehicle_operator', NAME: 'Commercial Vehicle Operator', RECORD_TYPE: 'MANDATORY', SUMMARY_SECTION: 'Mandatory Training', TOOLBOX_ELIGIBLE: false },
  { CODE: 'back_hoe', NAME: 'Back Hoe', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'club_car', NAME: 'Club Car', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'fork_lift', NAME: 'Fork Lift', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'gas_powered_equipment', NAME: 'Gas Powered Equipment', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'grader', NAME: 'Grader', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'ice_resurfacer', NAME: 'Ice Resurfacer', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'kubota_rtv', NAME: 'Kubota RTV', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'kubota_zero_turn', NAME: 'Kubota Zero Turn', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'light_truck_car_operations', NAME: 'Light Truck/Car Operations', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'loader', NAME: 'Loader', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'skidsteer', NAME: 'Skidsteer', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'street_sweeper', NAME: 'Street Sweeper', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'toolcat', NAME: 'Toolcat', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'toro', NAME: 'Toro', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'willie', NAME: 'Willie', RECORD_TYPE: 'COMPETENCY', SUMMARY_SECTION: 'Competency Assessments', TOOLBOX_ELIGIBLE: false },
  { CODE: 'diplomas_or_degrees', NAME: 'Diplomas or Degrees', RECORD_TYPE: 'EDUCATION', SUMMARY_SECTION: 'Education and Certificates', TOOLBOX_ELIGIBLE: false },
  { CODE: 'drivers_license_abstracts', NAME: 'Drivers License Abstracts', RECORD_TYPE: 'EDUCATION', SUMMARY_SECTION: 'Education and Certificates', TOOLBOX_ELIGIBLE: false },
  { CODE: '3_worker_rights', NAME: '3 Worker Rights', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'ab_ohs_legislation', NAME: 'AB OHS Legislation', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'animal_people_encounters', NAME: 'Animal & People Encounters', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'chainsaw_safety', NAME: 'Chainsaw Safety', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'confined_space_entry_gas_detection', NAME: 'Confined Space Entry & Gas Detection', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'due_diligence', NAME: 'Due Diligence', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'emergency_response_procedures_fire_marshal_trainings', NAME: 'Emergency Response Procedures, Fire Marshall Trainings', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'ergonomics', NAME: 'Ergonomics', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'fall_protection_plan', NAME: 'Fall Protection Plan', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'fire_extinguisher_training', NAME: 'Fire Extinguisher Training', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'harassment_violence_policy_procedures', NAME: 'Harassment & Violence Policy & Procedures', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'health_safety_policy', NAME: 'Health and Safety Policy', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'housekeeping', NAME: 'Housekeeping', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'incident_near_miss_reporting', NAME: 'Incident & Near Miss Reporting', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'lock_out_tag_out', NAME: 'Lock Out/Tag Out', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'robbery_prevention', NAME: 'Robbery Prevention', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'safe_driving_practices', NAME: 'Safe Driving Practices', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'safe_work_procedures', NAME: 'Safe Work Procedures', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'safety_committee_training', NAME: 'Safety Committee Training', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'slips_trips_falls', NAME: 'Slips, trips and falls', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'supervisors_ohs_responsibilities', NAME: 'Supervisors OHS Responsibilities', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'tick_awareness', NAME: 'Tick Awareness', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'working_alone_call_in_procedures', NAME: 'Working Alone, Call-in procedures', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'working_outdoors_heat_cold_procedures', NAME: 'Working Outdoors, Heat/Cold Procedures', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'workplace_inspections', NAME: 'Workplace Inspections', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true },
  { CODE: 'other', NAME: 'Other', RECORD_TYPE: 'OTHER', SUMMARY_SECTION: 'Other Training', TOOLBOX_ELIGIBLE: true }
];

const getCategoryRows = (categories) => Array.isArray(categories) ? categories : [];
const getCategoryName = (category) => category?.NAME || category?.name || '';
const getCategoryRecordType = (category) => category?.RECORD_TYPE || category?.recordType || '';
const getEmployeeName = (employee) => employee?.name || `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim();
const getUserDisplayName = (user) => [user?.firstName || user?.FIRST_NAME, user?.lastName || user?.LAST_NAME].filter(Boolean).join(' ').trim()
  || user?.displayName
  || user?.DISPLAY_NAME
  || user?.username
  || user?.USERNAME
  || user?.email
  || user?.EMAIL
  || '';
const getUserDepartmentNames = (user) => [...new Set([
  user?.department,
  user?.departmentName,
  user?.DEPARTMENT,
  ...(Array.isArray(user?.departments) ? user.departments.flatMap((department) => typeof department === 'string' ? [department] : [department?.name, department?.department, department?.DEPARTMENT, department?.NAME]) : []),
  ...(Array.isArray(user?.DEPARTMENTS) ? user.DEPARTMENTS.flatMap((department) => typeof department === 'string' ? [department] : [department?.name, department?.department, department?.DEPARTMENT, department?.NAME]) : [])
].map((value) => String(value || '').trim()).filter(Boolean))];
const formatTrainingSummaryDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString();
};
const normalizeSummaryValue = (value) => String(value || '').trim().toLowerCase();
const normalizeSummaryDateKey = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return normalizeSummaryValue(value);
  return parsed.toISOString().slice(0, 10);
};
const normalizeDepartmentDisplayLabel = (value) => String(value || '').trim().replace(/\s+/g, ' ');
const normalizeDepartmentPartKey = (value) => {
  const key = normalizeDepartmentDisplayLabel(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (['park', 'parks'].includes(key)) return 'parks';
  if (['road', 'roads', 'road operations', 'roads operations', 'road and operations', 'roads and operations', 'operations', 'public works'].includes(key)) return 'roads';
  if (['utility', 'utilities'].includes(key)) return 'utilities';
  return key;
};
const splitDepartmentDisplayParts = (value) => normalizeDepartmentDisplayLabel(value)
  .split(/\s*(?:,|&|\band\b)\s*/i)
  .map(normalizeDepartmentPartKey)
  .filter(Boolean);
const formatDepartmentAccessList = (departmentNames = []) => {
  const uniqueDepartments = [];
  const seenDepartments = new Set();

  departmentNames.forEach((departmentName) => {
    const label = normalizeDepartmentDisplayLabel(departmentName);
    const key = label.toLowerCase();
    if (!label || seenDepartments.has(key)) return;
    seenDepartments.add(key);
    uniqueDepartments.push(label);
  });

  const groupedDepartmentEntries = uniqueDepartments
    .filter((departmentName) => departmentName.includes(','))
    .map((departmentName) => ({
      label: departmentName,
      parts: [...new Set(splitDepartmentDisplayParts(departmentName))]
    }));
  const groupedDepartments = groupedDepartmentEntries
    .filter((entry, index) => !groupedDepartmentEntries.some((otherEntry, otherIndex) => otherIndex !== index
      && otherEntry.parts.length > entry.parts.length
      && entry.parts.every((part) => otherEntry.parts.includes(part))))
    .map((entry) => entry.label);
  const groupedDepartmentParts = new Set(groupedDepartments.flatMap(splitDepartmentDisplayParts));
  const displayDepartments = [
    ...groupedDepartments,
    ...uniqueDepartments.filter((departmentName) => {
      if (departmentName.includes(',')) return false;
      const departmentParts = splitDepartmentDisplayParts(departmentName);
      return departmentParts.length === 0 || !departmentParts.every((part) => groupedDepartmentParts.has(part));
    })
  ];

  return displayDepartments.join(displayDepartments.some((departmentName) => departmentName.includes(',')) ? '; ' : ', ');
};
const dedupeTrainingSummaryRecords = (records = []) => {
  const seen = new Set();

  return records.filter((record) => {
    const key = [
      normalizeSummaryValue(record.employeeEmail),
      normalizeSummaryValue(record.employeeName),
      normalizeSummaryValue(record.departmentName),
      normalizeSummaryValue(record.summarySection),
      normalizeSummaryValue(record.courseCategoryCode),
      normalizeSummaryValue(record.courseCategoryName),
      normalizeSummaryValue(record.courseDescription),
      normalizeSummaryDateKey(record.completionDate),
      normalizeSummaryDateKey(record.expiryDate),
      normalizeSummaryDateKey(record.reviewDate),
      normalizeSummaryDateKey(record.nextReviewDate),
      normalizeSummaryValue(record.status),
      normalizeSummaryValue(record.sourceType),
      normalizeSummaryValue(record.sourceRefId),
      normalizeSummaryValue(record.certificateUrl),
      normalizeSummaryValue(record.toolboxMeetingLink)
    ].join('|');

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const DEFAULT_TRAINING_SUMMARY_FILTERS = {
  employee: '',
  department: '',
  categoryCode: '',
  fromDate: '',
  toDate: '',
  expiryWindow: '',
  status: 'APPROVED'
};

const groupTrainingSummaryRecords = (records = []) => {
  const employeeMap = new Map();
  const normalizeIdentity = (value) => String(value || '').trim().toLowerCase();
  const nameToEmail = new Map();

  records.forEach((record) => {
    const name = normalizeIdentity(record.employeeName);
    const email = normalizeIdentity(record.employeeEmail);
    if (name && email && !nameToEmail.has(name)) {
      nameToEmail.set(name, email);
    }
  });

  const getEmployeeKey = (record) => {
    const email = normalizeIdentity(record.employeeEmail);
    if (email) return `email:${email}`;
    const name = normalizeIdentity(record.employeeName || 'unknown');
    const emailForName = nameToEmail.get(name);
    if (emailForName) return `email:${emailForName}`;
    return `name:${name}`;
  };
  const preferDisplayName = (currentName, nextName) => {
    const current = String(currentName || '').trim();
    const next = String(nextName || '').trim();
    if (!current || current.toLowerCase() === 'unknown employee') return next || current;
    if (next && next.length > current.length && next.toUpperCase() !== next) return next;
    return current;
  };

  records.forEach((record) => {
    const employeeKey = getEmployeeKey(record);
    if (!employeeMap.has(employeeKey)) {
      employeeMap.set(employeeKey, {
        employeeEmail: record.employeeEmail || null,
        employeeName: record.employeeName || record.employeeEmail || 'Unknown employee',
        departmentName: record.departmentName || null,
        records: [],
        sections: new Map()
      });
    }

    const employeeGroup = employeeMap.get(employeeKey);
    employeeGroup.employeeEmail = employeeGroup.employeeEmail || record.employeeEmail || null;
    employeeGroup.employeeName = preferDisplayName(employeeGroup.employeeName, record.employeeName || record.employeeEmail || 'Unknown employee');
    employeeGroup.departmentName = employeeGroup.departmentName || record.departmentName || null;
    employeeGroup.records.push(record);

    const sectionName = record.summarySection || 'Other Training';
    if (!employeeGroup.sections.has(sectionName)) {
      employeeGroup.sections.set(sectionName, []);
    }
    employeeGroup.sections.get(sectionName).push(record);
  });

  return Array.from(employeeMap.values()).map((employee) => ({
    ...employee,
    sections: Object.fromEntries(employee.sections.entries()),
    expiryWarnings: employee.records.filter((record) => record.expiryState === 'AMBER').length,
    expiredCount: employee.records.filter((record) => record.expiryState === 'RED').length
  }));
};

// ─── Sub-components ──────────────────────────────────────────────────────────
function Section({title,badge,children,collapsible=true}) {
  const [open,setOpen] = useState(true);
  return (
    <div style={{...CARD,overflow:"hidden"}}>
      <div style={{display:"flex",alignItems:"center",gap:"8px",padding:"11px 14px",
        cursor:collapsible?"pointer":"default",background: open ? TB : "var(--color-background-primary)",
        borderBottom: open ? "0.5px solid var(--color-border-tertiary)" : "none"}}
        onClick={() => collapsible && setOpen(o => !o)}>
        {collapsible && <span style={{fontSize:"11px",color:TK}}>{open?"▾":"▸"}</span>}
        <span style={{fontWeight:"500",fontSize:"14px",color:"var(--color-text-primary)",flex:1}}>{title}</span>
        {badge && <Badge c={badge.c} t={badge.t} />}
      </div>
      {open && <div style={{padding:"14px"}}>{children}</div>}
    </div>
  );
}

function Field({label,type="text",placeholder,options,rows,note,required, name, value, defaultValue, onChange, checked, defaultChecked, accept, disabled=false}) {
  return (
    <div>
      <label style={lbl}>{label}{required && <span style={{color:"#ef4444",marginLeft:"2px"}}>*</span>}</label>
      {type==="select" ? (
        <select name={name} value={value} defaultValue={defaultValue} onChange={onChange} disabled={disabled} style={{...sel,opacity:disabled?0.75:1,cursor:disabled?"not-allowed":undefined}}><option value="">{placeholder||"Select..."}</option>
          {(options||[]).map(o=><option key={o}>{o}</option>)}
        </select>
      ) : type==="textarea" ? (
        <textarea name={name} value={value} defaultValue={defaultValue} onChange={onChange} style={{...ta,minHeight:`${(rows||3)*22}px`}} placeholder={placeholder} />
      ) : type==="date" ? (
        <input type="date" name={name} value={value} defaultValue={defaultValue} onChange={onChange} style={inp} />
      ) : type==="radio" ? (
        <div style={{display:"flex",gap:"16px",marginTop:"4px"}}>
          {(options||[]).map(o=>(
            <label key={o} style={{display:"flex",alignItems:"center",gap:"5px",fontSize:"13px",cursor:"pointer",color:"var(--color-text-primary)"}}>
              <input type="radio" name={name || label} checked={value === o ? true : undefined} defaultChecked={defaultChecked === o} onChange={onChange ? (event) => onChange(o, event) : undefined} style={{accentColor:T}} /> {o}
            </label>
          ))}
        </div>
      ) : type==="checkbox" ? (
        <label style={{...checkboxRow,fontSize:"13px",cursor:"pointer",color:"var(--color-text-primary)"}}>
          <input type="checkbox" name={name} checked={checked} defaultChecked={defaultChecked} onChange={onChange} style={checkboxInput} />
          <span style={checkboxText}>{placeholder}</span>
        </label>
      ) : type==="file" ? (
        <input type="file" name={name} accept={accept} onChange={onChange} style={{fontSize:"13px",color:"var(--color-text-primary)"}} />
      ) : (
        <input type={type} name={name} value={value} defaultValue={defaultValue} onChange={onChange} style={inp} placeholder={placeholder} />
      )}
      {note && <p style={{fontSize:"11px",color:GR,margin:"3px 0 0",lineHeight:"1.4"}}>{note}</p>}
    </div>
  );
}

function FormActions({draft=true,submit="Submit", onDraft, onSubmit, busy = false, autosaveStatus = ''}) {
  return (
    <div style={{display:"flex",gap:"10px",paddingTop:"12px",borderTop:"0.5px solid var(--color-border-tertiary)",marginTop:"4px",flexWrap:"wrap"}}>
      {draft && <button type="button" onClick={(event) => onDraft?.(event)} disabled={busy} style={btn()}> Save as Draft</button>}
      {autosaveStatus && <span style={{fontSize:"11px",color:GR,alignSelf:"center"}}>{autosaveStatus}</span>}
      <button type="button" onClick={(event) => onSubmit?.(event)} disabled={busy} style={{...btn(T,"#fff",false),marginLeft:"auto",opacity: busy ? 0.75 : 1}}> {busy ? 'Saving...' : submit}</button>
    </div>
  );
}

function MultiSelectDropdown({ label, options, selected, onToggle, placeholder }) {
  const [open, setOpen] = useState(false);
  const display = selected.length === 0 ? placeholder : selected.join(', ');

  return (
    <div style={{ position: 'relative' }}>
      <label style={lbl}>{label}</label>
      <div
        onClick={() => setOpen((current) => !current)}
        style={{
          ...inp,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
          background: 'var(--color-background-primary)',
          color: 'var(--color-text-primary)'
        }}
      >
        <span style={{
          color: selected.length ? 'var(--color-text-primary)' : GR,
          fontSize: '13px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {display}
        </span>
        <span style={{ color: GR, fontSize: '11px', flexShrink: 0 }}>▾</span>
      </div>
      {open && (
        <>
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 200,
            background: '#ffffff',
            border: '0.5px solid var(--color-border-secondary)',
            borderRadius: 'var(--border-radius-md)',
            marginTop: '3px',
            maxHeight: '220px',
            overflowY: 'auto',
            boxShadow: '0 8px 20px rgba(0,0,0,0.08)'
          }}>
            {options.length > 0 ? options.map((option) => (
              <label
                key={option}
                style={{
                  ...checkboxRow,
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: 'var(--color-text-primary)',
                  background: selected.includes(option) ? TB : '#ffffff'
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => onToggle(option)}
                  style={checkboxInput}
                />
                <span style={checkboxText}>{option}</span>
              </label>
            )) : (
              <div style={{ padding: '10px 12px', fontSize: '13px', color: GR }}>No options available</div>
            )}
          </div>
          <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setOpen(false)} />
        </>
      )}
    </div>
  );
}

function TrainingSummaryEmployeeCard({ employee }) {
  const [open, setOpen] = useState({ mandatory: true, education: false, other: false, competency: false });

  const employeeInitials = String(employee.employeeName || employee.employeeEmail || 'U')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'U';

  const sectionOrder = [
    ['mandatory', 'Mandatory Training', 'Mandatory Training Completions'],
    ['education', 'Education and Certificates', 'Education and Certificates'],
    ['competency', 'Competency Assessments', 'Competency Assessments'],
    ['other', 'Other Training', 'Other Training — Non-Mandatory']
  ];

  const renderSectionRows = (sectionName, records = []) => {
    if (sectionName === 'Competency Assessments') {
      return records.map((record) => (
        <tr key={record.id} style={{background: record.expiryState === 'AMBER' ? '#fffbeb' : record.expiryState === 'RED' ? '#fef2f2' : 'transparent'}}>
          <td style={{padding:"6px 8px",fontWeight:"500",color:"var(--color-text-primary)"}}>{record.courseCategoryName || record.courseCategoryCode || 'N/A'}</td>
          <td style={{padding:"6px 8px",color:GR,maxWidth:"180px"}}>{record.courseDescription || 'N/A'}</td>
          <td style={{padding:"6px 8px",whiteSpace:"nowrap"}}>{formatTrainingSummaryDate(record.reviewDate || record.completionDate)}</td>
          <td style={{padding:"6px 8px",whiteSpace:"nowrap",color: record.expiryState === 'RED' ? '#b91c1c' : record.expiryState === 'AMBER' ? '#b45309' : 'var(--color-text-primary)'}}>{formatTrainingSummaryDate(record.nextReviewDate || record.expiryDate)}</td>
          <td style={{padding:"6px 8px"}}>{record.certificateUrl ? <a href={record.certificateUrl} target="_blank" rel="noreferrer" style={{color:T,textDecoration:"underline"}}>View PDF</a> : <span style={{color:GR}}>—</span>}</td>
        </tr>
      ));
    }

    if (sectionName === 'Other Training') {
      return records.map((record) => (
        <tr key={record.id} style={{background: record.expiryState === 'AMBER' ? '#fffbeb' : record.expiryState === 'RED' ? '#fef2f2' : 'transparent'}}>
          <td style={{padding:"6px 8px",fontWeight:"500",color:"var(--color-text-primary)"}}>{record.courseCategoryName || record.courseCategoryCode || 'N/A'}</td>
          <td style={{padding:"6px 8px",color:GR,maxWidth:"180px"}}>{record.courseDescription || 'N/A'}</td>
          <td style={{padding:"6px 8px",whiteSpace:"nowrap"}}>{formatTrainingSummaryDate(record.completionDate)}</td>
          <td style={{padding:"6px 8px",whiteSpace:"nowrap",color: record.expiryState === 'RED' ? '#b91c1c' : record.expiryState === 'AMBER' ? '#b45309' : 'var(--color-text-primary)'}}>{formatTrainingSummaryDate(record.expiryDate)}</td>
          <td style={{padding:"6px 8px"}}>
            {record.toolboxMeetingLink ? <a href={record.toolboxMeetingLink} style={{color:T,textDecoration:"underline"}}>→ Toolbox Meeting</a> : record.certificateUrl ? <a href={record.certificateUrl} target="_blank" rel="noreferrer" style={{color:T,textDecoration:"underline"}}>View PDF</a> : <span style={{color:GR}}>—</span>}
          </td>
        </tr>
      ));
    }

    return records.map((record) => (
      <tr key={record.id} style={{background: record.expiryState === 'AMBER' ? '#fffbeb' : record.expiryState === 'RED' ? '#fef2f2' : 'transparent'}}>
        <td style={{padding:"6px 8px",fontWeight:"500",color:"var(--color-text-primary)"}}>{record.courseCategoryName || record.courseCategoryCode || 'N/A'}</td>
        <td style={{padding:"6px 8px",color:GR,maxWidth:"180px"}}>{record.courseDescription || 'N/A'}</td>
        <td style={{padding:"6px 8px",whiteSpace:"nowrap"}}>{formatTrainingSummaryDate(record.completionDate)}</td>
        <td style={{padding:"6px 8px",whiteSpace:"nowrap",color: record.expiryState === 'RED' ? '#b91c1c' : record.expiryState === 'AMBER' ? '#b45309' : 'var(--color-text-primary)'}}>{formatTrainingSummaryDate(record.expiryDate)}</td>
        <td style={{padding:"6px 8px"}}>{record.certificateUrl ? <a href={record.certificateUrl} target="_blank" rel="noreferrer" style={{color:T,textDecoration:"underline"}}>View PDF</a> : <span style={{color:GR}}>—</span>}</td>
      </tr>
    ));
  };

  return (
    <div style={{...CARD}}>
      <div style={{padding:"12px 14px",borderBottom:"0.5px solid var(--color-border-tertiary)",display:"flex",alignItems:"center",gap:"10px",flexWrap:"wrap"}}>
        <div style={{width:"36px",height:"36px",borderRadius:"50%",background:TB,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:"500",fontSize:"13px",color:TK}}>{employeeInitials}</div>
        <div>
          <div style={{fontWeight:"500",fontSize:"14px",color:"var(--color-text-primary)"}}>{employee.employeeName || employee.employeeEmail || 'Unknown employee'}</div>
          <div style={{fontSize:"12px",color:GR}}>{employee.departmentName || 'Department not recorded'}</div>
        </div>
        <div style={{marginLeft:"auto",display:"flex",gap:"6px",flexWrap:"wrap"}}>
          <Badge c="teal" t={`${employee.records.length} records`} />
          {employee.expiredCount > 0 && <Badge c="amber" t={`${employee.expiredCount} Expired`} />}
          {employee.expiryWarnings > 0 && <Badge c="amber" t={`${employee.expiryWarnings} Expiring Soon`} />}
        </div>
      </div>

      <div style={{padding:"0 14px"}}>
        {sectionOrder.map(([stateKey, sectionName, heading]) => {
          const sectionRecords = employee.sections?.[sectionName] || [];
          if (sectionRecords.length === 0) {
            return null;
          }

          const columnHeaders = sectionName === 'Competency Assessments'
            ? ['Equipment / Category', 'Description', 'Last Review', 'Next Review', 'Assessment Report']
            : sectionName === 'Education and Certificates'
              ? ['Course Category', 'Description', 'Date', 'Expiry', 'Certificate']
              : sectionName === 'Other Training'
                ? ['Course Category', 'Description', 'Completion Date', 'Expiry', 'Certificate / Toolbox Link']
                : ['Course Category', 'Course Description', 'Completion Date', 'Expiry Date', 'Certificate'];

          return (
            <div key={sectionName}>
              <div style={{display:"flex",alignItems:"center",gap:"8px",padding:"10px 0",cursor:"pointer",borderBottom:"0.5px solid var(--color-border-tertiary)"}}
                onClick={() => setOpen((current) => ({ ...current, [stateKey]: !current[stateKey] }))}>
                <span style={{fontSize:"11px",color:TK}}>{open[stateKey] ? "▾" : "▸"}</span>
                <span style={{fontWeight:"500",fontSize:"13px",color:"var(--color-text-primary)"}}>{heading}</span>
                <span style={{marginLeft:"auto",fontSize:"12px",color:GR}}>{sectionRecords.length} record{sectionRecords.length === 1 ? '' : 's'}</span>
              </div>
              {open[stateKey] && (
                <div style={{overflowX:"auto",paddingBottom:"8px"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px",marginTop:"6px"}}>
                    <thead>
                      <tr>{columnHeaders.map((header) => (
                        <th key={header} style={{textAlign:"left",padding:"5px 8px",color:GR,fontWeight:"500",borderBottom:"0.5px solid var(--color-border-tertiary)",whiteSpace:"nowrap"}}>{header}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {renderSectionRows(sectionName, sectionRecords)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── SCREEN 1: Training Summary ──────────────────────────────────────────────
function TrainingSummary() {
  const [bootstrap, setBootstrap] = useState({ currentUser: null, employees: [], departments: [], courseCategories: [], trainingSummaryAccess: null });
  const [summaryRecords, setSummaryRecords] = useState([]);
  const [summaryAccessScope, setSummaryAccessScope] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [searchFilters, setSearchFilters] = useState(DEFAULT_TRAINING_SUMMARY_FILTERS);

  useEffect(() => {
    let cancelled = false;

    const loadBootstrap = async () => {
      try {
        const response = await API.get('/api/training/bootstrap');
        if (!cancelled && response.data?.success) {
          setBootstrap({
            currentUser: response.data.currentUser || null,
            employees: response.data.employees || [],
            departments: response.data.departments || [],
            courseCategories: response.data.courseCategories || [],
            trainingSummaryAccess: response.data.trainingSummaryAccess || null
          });
        }
      } catch (error) {
        if (!cancelled) {
          setBootstrap({
            currentUser: null,
            employees: [],
            departments: [],
            courseCategories: FALLBACK_TRAINING_CATEGORIES,
            trainingSummaryAccess: null
          });
        }
      }
    };

    loadBootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      setSummaryLoading(true);
      setSummaryError('');
      try {
        const params = {};
        if (DEFAULT_TRAINING_SUMMARY_FILTERS.status) params.status = DEFAULT_TRAINING_SUMMARY_FILTERS.status;

        const response = await API.get('/api/training/summary', { params });
        if (!cancelled) {
          setSummaryRecords(Array.isArray(response.data?.records) ? response.data.records : []);
          setSummaryAccessScope(response.data?.accessScope || null);
        }
      } catch (error) {
        if (!cancelled) {
          setSummaryRecords([]);
          setSummaryError(error?.response?.data?.error || error.message || 'Unable to load training summary.');
        }
      } finally {
        if (!cancelled) {
          setSummaryLoading(false);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const activeAccessScope = summaryAccessScope || bootstrap.trainingSummaryAccess || {};
    if (activeAccessScope.scope === 'own' && (searchFilters.employee || searchFilters.department)) {
      setSearchFilters((previous) => ({ ...previous, employee: '', department: '' }));
    }
  }, [summaryAccessScope, bootstrap.trainingSummaryAccess, searchFilters.employee, searchFilters.department]);

  const employees = bootstrap.employees.length > 0 ? bootstrap.employees : [];
  const visibleSummaryRecords = dedupeTrainingSummaryRecords(summaryRecords);
  const activeAccessScope = summaryAccessScope || bootstrap.trainingSummaryAccess || {};
  const accessScopeType = activeAccessScope.scope || 'all';
  const accessDepartments = Array.isArray(activeAccessScope.departments) ? activeAccessScope.departments.filter(Boolean) : [];
  const currentUserDisplayName = getUserDisplayName(bootstrap.currentUser);
  const currentUserDepartments = getUserDepartmentNames(bootstrap.currentUser);
  const fallbackDepartments = bootstrap.departments.length > 0
    ? bootstrap.departments.map((department) => department.name).filter(Boolean)
    : Array.from(new Set(visibleSummaryRecords.map((record) => record.departmentName).filter(Boolean)));
  const departments = accessScopeType === 'own'
    ? [...new Set([...accessDepartments, ...currentUserDepartments].filter(Boolean))]
    : accessScopeType === 'department' && accessDepartments.length > 0
      ? accessDepartments
      : fallbackDepartments;
  const categories = getCategoryRows(bootstrap.courseCategories.length > 0 ? bootstrap.courseCategories : FALLBACK_TRAINING_CATEGORIES);
  const allCategoryOptions = categories.map(getCategoryName).filter(Boolean).sort();
  const ownEmployeeOptions = [...new Set([
    currentUserDisplayName,
    activeAccessScope.employeeName,
    activeAccessScope.employeeEmail,
    bootstrap.currentUser?.email,
    bootstrap.currentUser?.username
  ].filter(Boolean))];
  const employeeOptions = accessScopeType === 'own'
    ? ownEmployeeOptions
    : employees.map(getEmployeeName).filter(Boolean).sort();
  const departmentAccessList = formatDepartmentAccessList(departments);
  const accessScopeMessage = accessScopeType === 'own'
    ? `Privacy enabled: you can only view ${currentUserDisplayName ? `${currentUserDisplayName}'s` : 'your'} training records.`
    : accessScopeType === 'department'
      ? `Privacy enabled: supervisors and managers can view only ${departmentAccessList || 'their department'} training records.`
      : 'Privacy enabled: training administrators can view all training records.';
  const summaryEmployees = groupTrainingSummaryRecords(visibleSummaryRecords).sort((left, right) => {
    const leftLabel = String(left.employeeName || left.employeeEmail || '').toLowerCase();
    const rightLabel = String(right.employeeName || right.employeeEmail || '').toLowerCase();
    return leftLabel.localeCompare(rightLabel);
  });
  const totalRecords = visibleSummaryRecords.length;
  const totalEmployees = summaryEmployees.length;

  const handleSearch = async (event) => {
    event?.preventDefault?.();
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const params = {};
      if (searchFilters.employee.trim()) params.employee = searchFilters.employee.trim();
      if (searchFilters.department.trim()) params.department = searchFilters.department.trim();
      if (searchFilters.categoryCode.trim()) params.categoryCode = searchFilters.categoryCode.trim();
      if (searchFilters.fromDate) params.fromDate = searchFilters.fromDate;
      if (searchFilters.toDate) params.toDate = searchFilters.toDate;
      if (searchFilters.expiryWindow) params.expiryWindow = searchFilters.expiryWindow;
      if (searchFilters.status) params.status = searchFilters.status;

      const response = await API.get('/api/training/summary', { params });
      setSummaryRecords(Array.isArray(response.data?.records) ? response.data.records : []);
      setSummaryAccessScope(response.data?.accessScope || null);
    } catch (error) {
      setSummaryRecords([]);
      setSummaryError(error?.response?.data?.error || error.message || 'Unable to load training summary.');
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <div>
      <div style={{background:TB,border:`0.5px solid ${T}`,borderRadius:"var(--border-radius-md)",padding:"10px 14px",marginBottom:"12px",fontSize:"13px",color:TK,lineHeight:"1.4"}}>
        <strong>Training Summary Privacy:</strong> {accessScopeMessage}
      </div>
      {/* Search Panel */}
      <Section title="Training Summary Search" collapsible={false}>
        <form onSubmit={handleSearch}>
          <div style={grid2}>
            <Field
              label="Employee"
              type="select"
              value={searchFilters.employee}
              onChange={(event) => setSearchFilters((previous) => ({ ...previous, employee: event.target.value }))}
              options={employeeOptions}
              placeholder={accessScopeType === 'own' ? 'My training records' : accessScopeType === 'department' ? 'All department employees' : 'All employees'}
              disabled={accessScopeType === 'own'}
              note={accessScopeType === 'own' ? 'Locked by privacy controls to the logged-in employee.' : 'Search real users within your permitted training summary scope.'}
            />
            <Field
              label="Department"
              type="select"
              value={searchFilters.department}
              onChange={(event) => setSearchFilters((previous) => ({ ...previous, department: event.target.value }))}
              options={departments}
              placeholder={accessScopeType === 'own' ? (departments[0] || 'My department') : accessScopeType === 'department' ? 'All assigned departments' : 'All departments'}
              disabled={accessScopeType === 'own'}
              note={accessScopeType === 'department' ? 'Limited to the department(s) assigned to your supervisor/manager profile.' : undefined}
            />
          </div>
          <div style={{...grid2,marginTop:"10px"}}>
            <Field
              label="Course Category"
              type="select"
              value={searchFilters.categoryCode}
              onChange={(event) => setSearchFilters((previous) => ({ ...previous, categoryCode: event.target.value }))}
              options={allCategoryOptions}
              placeholder="All categories"
            />
            <Field
              label="Status"
              type="select"
              value={searchFilters.status}
              onChange={(event) => setSearchFilters((previous) => ({ ...previous, status: event.target.value }))}
              options={["APPROVED", "SUBMITTED", "DRAFT", "REJECTED"]}
              placeholder="All statuses"
            />
          </div>
          <div style={{...grid2,marginTop:"10px"}}>
            <Field label="From Date" type="date" value={searchFilters.fromDate} onChange={(event) => setSearchFilters((previous) => ({ ...previous, fromDate: event.target.value }))} />
            <Field label="To Date" type="date" value={searchFilters.toDate} onChange={(event) => setSearchFilters((previous) => ({ ...previous, toDate: event.target.value }))} />
          </div>
          <div style={{...grid2,marginTop:"10px"}}>
            <Field label="Courses Expiring Within" type="select" value={searchFilters.expiryWindow} onChange={(event) => setSearchFilters((previous) => ({ ...previous, expiryWindow: event.target.value }))} options={["30","60","90","expired"]} placeholder="Any expiry window" note="Use 30, 60, 90, or expired." />
            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"flex-end"}}>
              <button type="submit" style={btn(T,"#fff",false)} disabled={summaryLoading}>{summaryLoading ? 'Searching...' : 'Search'}</button>
            </div>
          </div>
        </form>
        {summaryError && <div style={{marginTop:"10px",fontSize:"12px",color:"#b91c1c"}}>{summaryError}</div>}
      </Section>

      <div style={{...CARD,padding:"12px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"8px",flexWrap:"wrap"}}>
        <div>
          <div style={{fontWeight:"500",fontSize:"14px",color:"var(--color-text-primary)"}}>Live Training Results</div>
          <div style={{fontSize:"12px",color:GR}}>{totalEmployees} employee{totalEmployees === 1 ? '' : 's'} • {totalRecords} record{totalRecords === 1 ? '' : 's'}</div>
        </div>
        <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
          <Badge c="teal" t="Approved Summary" />
          <Badge c="blue" t="PDF Certificates" />
        </div>
      </div>

      {summaryLoading && <div style={{fontSize:"12px",color:GR,margin:"10px 0"}}>Loading live training summary...</div>}
      {!summaryLoading && summaryEmployees.length === 0 && <div style={{fontSize:"12px",color:GR,margin:"10px 0"}}>No live training records matched the current search.</div>}
      {!summaryLoading && summaryEmployees.map((employee) => <TrainingSummaryEmployeeCard key={`${employee.employeeEmail || ''}::${employee.employeeName || ''}`} employee={employee} />)}
    </div>
  );
}

// ─── SCREEN 2: Add Training Record ──────────────────────────────────────────
function AddTrainingRecord() {
  const [forOther,setForOther] = useState(false);
  const [hasExpiry,setHasExpiry] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [bootstrap, setBootstrap] = useState({ currentUser: null, employees: [], courseCategories: [] });
  const [formData, setFormData] = useState({
    employeeName: '',
    selectedEmployeeName: '',
    courseCategory: '',
    courseDescription: '',
    completionDate: '',
    expiryDate: '',
    supportingDocumentName: '',
    supportingDocumentFile: null
  });

  const restoreAddTrainingAutosave = useCallback((draft) => {
    if (!draft || typeof draft !== 'object') return;
    setForOther(Boolean(draft.forOther));
    setHasExpiry(Boolean(draft.hasExpiry));
    if (draft.formData && typeof draft.formData === 'object') {
      const savedFormData = { ...draft.formData };
      delete savedFormData.supportingDocumentFile;
      setFormData((previous) => ({ ...previous, ...savedFormData, supportingDocumentFile: null }));
    }
  }, []);

  const addTrainingAutosaveSnapshot = useCallback(() => ({
    forOther,
    hasExpiry,
    formData: {
      ...formData,
      supportingDocumentFile: null
    }
  }), [forOther, hasExpiry, formData]);
  const addTrainingAutosaveStatus = useTrainingAutosave('add-training-record', addTrainingAutosaveSnapshot, restoreAddTrainingAutosave);

  useEffect(() => {
    let cancelled = false;

    const loadBootstrap = async () => {
      try {
        const response = await API.get('/api/training/bootstrap');
        if (!cancelled && response.data?.success) {
          setBootstrap({
            currentUser: response.data.currentUser || null,
            employees: response.data.employees || [],
            courseCategories: response.data.courseCategories || []
          });

          const currentUserName = [response.data.currentUser?.firstName, response.data.currentUser?.lastName].filter(Boolean).join(' ')
            || response.data.currentUser?.displayName
            || response.data.currentUser?.username
            || '';

          setFormData((previous) => ({
            ...previous,
            employeeName: previous.employeeName || currentUserName,
            courseCategory: previous.courseCategory || '',
            courseDescription: previous.courseDescription || ''
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setBootstrap({ employees: [], courseCategories: FALLBACK_TRAINING_CATEGORIES });
        }
      }
    };

    loadBootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const employees = bootstrap.employees.length > 0 ? bootstrap.employees : [
    { name: 'Jill Ellis' },
    { name: 'Tom Bradley' },
    { name: 'Leah Gomez' },
    { name: 'Sandra Liu' }
  ];
  const categories = getCategoryRows(bootstrap.courseCategories.length > 0 ? bootstrap.courseCategories : FALLBACK_TRAINING_CATEGORIES);
  const allCategoryOptions = categories.map(getCategoryName).filter(Boolean).sort();
  const selectedEmployeeName = formData.selectedEmployeeName || '';
  const submitTrainingRecord = async (status) => {
    setIsSaving(true);
    try {
      const selectedEmployeeRecord = forOther
        ? employees.find((employee) => getEmployeeName(employee) === selectedEmployeeName) || null
        : null;
      if (forOther && !selectedEmployeeName) {
        throw new Error('Please select an employee before submitting the record for approval.');
      }
      if (!formData.courseCategory) {
        throw new Error('Please select a course category before saving the training record.');
      }
      if (!formData.supportingDocumentFile) {
        throw new Error('Please attach a required PDF or image supporting document before saving the training record.');
      }
      const category = categories.find((entry) => getCategoryName(entry) === formData.courseCategory) || categories[0] || {};
      const employeeName = forOther
        ? (selectedEmployeeRecord?.name || selectedEmployeeName || '')
        : (formData.employeeName || bootstrap.currentUser?.displayName || bootstrap.currentUser?.username || 'Unknown employee');
      const employeeEmail = forOther
        ? (selectedEmployeeRecord?.email || '')
        : (bootstrap.currentUser?.email || bootstrap.currentUser?.username || '');
      const departmentName = forOther
        ? (selectedEmployeeRecord?.department || bootstrap.currentUser?.department || bootstrap.currentUser?.departmentName || null)
        : (bootstrap.currentUser?.department || bootstrap.currentUser?.departmentName || null);

      const payload = new FormData();
      payload.append('employeeName', employeeName);
      if (employeeEmail) {
        payload.append('employeeEmail', employeeEmail);
      }
      if (departmentName) {
        payload.append('departmentName', departmentName);
      }
      payload.append('categoryId', category.ID || category.id || '');
      payload.append('categoryCode', category.CODE || category.code || '');
      payload.append('categoryName', category.NAME || category.name || formData.courseCategory || '');
      payload.append('recordType', category.RECORD_TYPE || category.recordType || 'OTHER');
      payload.append('summarySection', category.SUMMARY_SECTION || category.summarySection || 'Other Training');
      payload.append('courseDescription', formData.courseDescription || category.NAME || category.name || '');
      payload.append('completionDate', formData.completionDate || '');
      payload.append('expiryDate', hasExpiry ? (formData.expiryDate || '') : '');
      payload.append('status', status);
      payload.append('sourceType', 'MANUAL');
      payload.append('supportingDocument', formData.supportingDocumentFile);

      const response = await API.post('/api/training/records', payload);
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to save training record');
      }
      if (status === 'SUBMITTED') {
        window.alert('Training record submitted for approval.');
      } else {
        window.alert('Training draft saved.');
      }
    } catch (error) {
      window.alert(error?.response?.data?.error || error.message || 'Unable to save training record.');
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <div>
      <div style={{background:"#eff6ff",border:"0.5px solid #bfdbfe",borderRadius:"var(--border-radius-md)",padding:"10px 14px",marginBottom:"12px",fontSize:"13px",color:"#1d4ed8"}}>
        <strong>Workflow note:</strong> Submitted records are sent to the supervisor for review and approval before appearing on the Training Summary.
      </div>
      <Section title="Training Intake Form" collapsible={false}>
        <Field label="Employee Name" value={formData.employeeName} onChange={(event) => setFormData((previous) => ({ ...previous, employeeName: event.target.value }))} placeholder="Auto-populated from login" note="Defaults to the logged-in employee" />
        <div style={{marginTop:"10px"}}>
          <label style={lbl}>Adding a training record for another employee?</label>
          <div style={{display:"flex",gap:"12px",marginBottom: forOther?"10px":"0"}}>
            {["No","Yes"].map(v=>(
              <label key={v} style={{display:"flex",alignItems:"center",gap:"5px",fontSize:"13px",cursor:"pointer",color:"var(--color-text-primary)"}}>
                <input type="radio" name="forOther" checked={forOther === (v === "Yes")} onChange={()=>setForOther(v==="Yes")} style={{accentColor:T}} /> {v}
              </label>
            ))}
          </div>
          {forOther && <Field label="Select Employee" type="select" value={formData.selectedEmployeeName} onChange={(event) => setFormData((previous) => ({ ...previous, selectedEmployeeName: event.target.value }))} options={employees.map(getEmployeeName).filter(Boolean)} placeholder="Select employee" />}
        </div>
        <Divider />
        <div style={grid2}>
          <Field label="Course Category" type="select" value={formData.courseCategory} onChange={(event) => setFormData((previous) => ({ ...previous, courseCategory: event.target.value }))} options={allCategoryOptions} placeholder="Select a category" required note="Determines which Training Summary section this record is added to" />
          <Field label="Course Description" value={formData.courseDescription} onChange={(event) => setFormData((previous) => ({ ...previous, courseDescription: event.target.value }))} placeholder="Auto-populated or free-form if blank" required note="Required if category description is blank" />
        </div>
        <div style={{...grid2,marginTop:"10px"}}>
          <Field label="Completion Date" type="date" value={formData.completionDate} onChange={(event) => setFormData((previous) => ({ ...previous, completionDate: event.target.value }))} required />
          <div>
            <label style={lbl}>Expiry Date</label>
            <div style={{display:"flex",gap:"10px",marginBottom:"6px",alignItems:"center"}}>
              <span style={{fontSize:"13px",color:GR}}>N/A</span>
              <input type="checkbox" checked={hasExpiry} onChange={e=>setHasExpiry(e.target.checked)} style={{accentColor:T}} /><span style={{fontSize:"13px"}}>Set expiry date</span>
            </div>
            {hasExpiry && <input type="date" value={formData.expiryDate} onChange={(event) => setFormData((previous) => ({ ...previous, expiryDate: event.target.value }))} style={inp} />}
          </div>
        </div>
        <div style={{marginTop:"10px"}}>
          <Field label="Certificate / Supporting Document" type="file" required accept=".pdf,image/*" onChange={(event) => setFormData((previous) => ({ ...previous, supportingDocumentName: event.target.files?.[0]?.name || '', supportingDocumentFile: event.target.files?.[0] || null }))} note="Required PDF or image. This is appended to the Training Intake Form and displayed in the Training Summary certificate column." />
        </div>
        <Divider />
        <FormActions draft={false} submit="Submit for Approval" busy={isSaving} autosaveStatus={addTrainingAutosaveStatus} onSubmit={() => submitTrainingRecord('SUBMITTED')} />
      </Section>
    </div>
  );
}

// ─── SCREEN 3: Add Competency Record ────────────────────────────────────────
const RATING_OPTS = ["Exceeds Expectations","Meets Expectations","Inconsistent Performance","Expectations Not Met"];
const CONF_OPTS   = ["Yes","No","Unsure"];
const COMPETENCY_RATING_SECTIONS = [
  ["Safety Hazard Practices","Initiates actions to prevent hazard exposure; reports hazards; encourages safe practices in others"],
  ["Safety Behaviours","Boosts group morale; volunteers to help; accepts and suggests new safe work processes"],
  ["Jobs / Tasks Assessment","Demonstrates exceptional skill for safe work execution; communicates to improve others' skill level"]
];
const COMPETENCY_EVALUATION_QUESTIONS = [
  "I am confident that the employee has sufficient knowledge of safety processes and procedures.",
  "I am confident that the employee demonstrates high compliance to safety policies.",
  "I am confident that the employee demonstrates a high level of compliance and appropriate use of PPE.",
  "I am confident that the employee will take action to ensure co-workers also work safely."
];
const ERP_FALLBACK_DEPARTMENTS = [
  'Communications',
  'Corporate Services',
  'Legislative Services',
  'Municipal Enforcement',
  'Parks',
  'Planning and Development',
  'Recreation',
  'Roads and Operations',
  'Utilities'
];
function AddCompetencyRecord() {
  const [actions,setActions] = useState([{id:1,desc:"",assignedTo:"",targetDate:""}]);
  const [bootstrap, setBootstrap] = useState({ currentUser: null, employees: [], courseCategories: [] });
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    employeeName: '',
    courseCategory: '',
    courseDescription: '',
    assessmentDescription: '',
    reviewDate: '',
    nextReviewDate: '',
    assessorAcknowledgement: false,
    supportingDocumentName: '',
    supportingDocumentFile: null,
    performanceRatings: {},
    overallEvaluations: {}
  });

  const restoreCompetencyAutosave = useCallback((draft) => {
    if (!draft || typeof draft !== 'object') return;
    if (Array.isArray(draft.actions) && draft.actions.length > 0) {
      setActions(draft.actions);
    }
    if (draft.formData && typeof draft.formData === 'object') {
      const savedFormData = { ...draft.formData };
      delete savedFormData.supportingDocumentFile;
      setFormData((previous) => ({ ...previous, ...savedFormData, supportingDocumentFile: null }));
    }
  }, []);

  const competencyAutosaveSnapshot = useCallback(() => ({
    actions,
    formData: {
      ...formData,
      supportingDocumentFile: null
    }
  }), [actions, formData]);
  const competencyAutosaveStatus = useTrainingAutosave('add-competency', competencyAutosaveSnapshot, restoreCompetencyAutosave);

  useEffect(() => {
    let cancelled = false;

    const loadBootstrap = async () => {
      try {
        const response = await API.get('/api/training/bootstrap');
        if (!cancelled && response.data?.success) {
          setBootstrap({
            currentUser: response.data.currentUser || null,
            employees: response.data.employees || [],
            courseCategories: response.data.courseCategories || []
          });

          const currentUserName = [response.data.currentUser?.firstName, response.data.currentUser?.lastName].filter(Boolean).join(' ')
            || response.data.currentUser?.displayName
            || response.data.currentUser?.username
            || '';

          setFormData((previous) => ({
            ...previous,
            employeeName: previous.employeeName || currentUserName,
            courseCategory: previous.courseCategory || '',
            courseDescription: previous.courseDescription || ''
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setBootstrap({ employees: [], courseCategories: FALLBACK_TRAINING_CATEGORIES });
        }
      }
    };

    loadBootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const employees = bootstrap.employees.length > 0 ? bootstrap.employees : [
    { name: 'Jill Ellis' },
    { name: 'Tom Bradley' },
    { name: 'Leah Gomez' },
    { name: 'Doreen Von Gradulewski' }
  ];
  const competencyOptions = getCategoryRows(bootstrap.courseCategories.length > 0 ? bootstrap.courseCategories : FALLBACK_TRAINING_CATEGORIES)
    .filter((category) => getCategoryRecordType(category) === 'COMPETENCY')
    .map(getCategoryName)
    .filter(Boolean)
    .sort();
  const submitCompetencyRecord = async (status) => {
    setIsSaving(true);
    try {
      const availableCategories = getCategoryRows(bootstrap.courseCategories.length > 0 ? bootstrap.courseCategories : FALLBACK_TRAINING_CATEGORIES);
      const category = availableCategories.find((entry) => getCategoryName(entry) === formData.courseCategory)
        || availableCategories.find((entry) => getCategoryRecordType(entry) === 'COMPETENCY')
        || {};
      const selectedEmployeeRecord = employees.find((employee) => getEmployeeName(employee) === formData.employeeName) || null;
      const employeeName = selectedEmployeeRecord?.name || formData.employeeName || bootstrap.currentUser?.displayName || bootstrap.currentUser?.username || 'Unknown employee';
      const employeeEmail = selectedEmployeeRecord?.email || bootstrap.currentUser?.email || bootstrap.currentUser?.username || '';
      const employeeDepartment = selectedEmployeeRecord?.department || bootstrap.currentUser?.department || bootstrap.currentUser?.departmentName || '';
      const assessorName = bootstrap.currentUser?.displayName
        || [bootstrap.currentUser?.firstName, bootstrap.currentUser?.lastName].filter(Boolean).join(' ')
        || bootstrap.currentUser?.username
        || bootstrap.currentUser?.email
        || 'Unknown assessor';
      const correctiveActionPayload = actions
        .filter((action) => String(action.desc || '').trim())
        .map((action) => {
          const assignedEmployee = employees.find((employee) => getEmployeeName(employee) === action.assignedTo) || null;
          return {
            description: action.desc,
            assignedToName: action.assignedTo,
            assignedToEmail: assignedEmployee?.email || '',
            targetDate: action.targetDate
          };
        });
      const questionnaire = {
        employeeName,
        employeeEmail,
        employeeDepartment,
        assessorName,
        courseCategory: formData.courseCategory,
        courseDescription: formData.courseDescription,
        assessmentDescription: formData.assessmentDescription,
        reviewDate: formData.reviewDate,
        nextReviewDate: formData.nextReviewDate,
        performanceRatings: formData.performanceRatings,
        overallEvaluations: formData.overallEvaluations,
        assessorAcknowledgement: formData.assessorAcknowledgement
      };
      const payload = new FormData();
      payload.append('employeeName', employeeName);
      if (employeeEmail) {
        payload.append('employeeEmail', employeeEmail);
      }
      if (employeeDepartment) {
        payload.append('employeeDepartment', employeeDepartment);
      }
      if (bootstrap.currentUser?.email || bootstrap.currentUser?.username) {
        payload.append('assessorEmail', bootstrap.currentUser?.email || bootstrap.currentUser?.username || '');
      }
      payload.append('assessorName', assessorName);
      payload.append('categoryId', category.ID || category.id || '');
      payload.append('categoryCode', category.CODE || category.code || '');
      payload.append('categoryName', category.NAME || category.name || formData.courseCategory || '');
      payload.append('recordType', 'COMPETENCY');
      payload.append('summarySection', 'Competency Assessments');
      payload.append('courseDescription', formData.courseDescription || formData.assessmentDescription || category.NAME || category.name || '');
      payload.append('assessmentDate', formData.reviewDate || '');
      payload.append('reviewDate', formData.reviewDate || '');
      payload.append('nextReviewDate', formData.nextReviewDate || '');
      payload.append('status', status);
      payload.append('sourceType', 'MANUAL');
      payload.append('assessorAcknowledgement', String(!!formData.assessorAcknowledgement));
      payload.append('questionnaire', JSON.stringify(questionnaire));
      payload.append('correctiveActions', JSON.stringify(correctiveActionPayload));
      if (formData.supportingDocumentFile) {
        payload.append('supportingDocument', formData.supportingDocumentFile);
      }

      const response = await API.post('/api/competencyAssessments/records', payload);
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to save competency record');
      }
      window.alert(status === 'SUBMITTED' ? 'Competency assessment submitted for final approval.' : 'Competency draft saved.');
    } catch (error) {
      window.alert(error?.response?.data?.error || error.message || 'Unable to save competency assessment.');
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <div>
      <div style={{background:"#eff6ff",border:"0.5px solid #bfdbfe",borderRadius:"var(--border-radius-md)",padding:"10px 14px",marginBottom:"12px",fontSize:"13px",color:"#1d4ed8"}}>
        <strong>Workflow note:</strong> Completed assessment is sent to the supervisor for final review and approval. Employee receives an email link to the approved form.
      </div>
      <Section title="Competency Assessment Record" collapsible={false}>
        <div style={grid2}>
          <Field label="Employee Name" type="select" value={formData.employeeName} onChange={(event) => setFormData((previous) => ({ ...previous, employeeName: event.target.value }))} options={employees.map(getEmployeeName).filter(Boolean)} placeholder="Select employee" required />
          <Field label="Course Category (Equipment / Task)" type="select" value={formData.courseCategory} onChange={(event) => setFormData((previous) => ({ ...previous, courseCategory: event.target.value }))} options={competencyOptions} placeholder="Select or add Other" required note="Links to Competency Assessment section of Training Summary" />
        </div>
        <div style={{...grid2,marginTop:"10px"}}>
          <Field label="Course Description" value={formData.courseDescription} onChange={(event) => setFormData((previous) => ({ ...previous, courseDescription: event.target.value }))} placeholder="Auto-populated; free-form for 'Other'" note="Links to Course Description in Training Summary" />
          <Field label="Assessment Description" type="textarea" value={formData.assessmentDescription} onChange={(event) => setFormData((previous) => ({ ...previous, assessmentDescription: event.target.value }))} placeholder="Briefly describe the job tasks assessed..." rows={2} />
        </div>
        <Divider />
        <p style={{...sec,fontSize:"14px"}}>Performance Ratings</p>
        {COMPETENCY_RATING_SECTIONS.map(([title,desc])=>(
          <div key={title} style={{marginBottom:"12px"}}>
            <div style={{fontSize:"13px",fontWeight:"500",color:"var(--color-text-primary)",marginBottom:"3px"}}>{title}</div>
            <div style={{fontSize:"11px",color:GR,marginBottom:"6px",lineHeight:"1.4"}}>{desc}</div>
            <Field
              label=""
              type="select"
              value={formData.performanceRatings[title] || ''}
              onChange={(event) => setFormData((previous) => ({
                ...previous,
                performanceRatings: {
                  ...previous.performanceRatings,
                  [title]: event.target.value
                }
              }))}
              options={RATING_OPTS}
              placeholder="Select rating"
            />
          </div>
        ))}
        <Divider />
        <p style={{...sec,fontSize:"14px"}}>Review Dates</p>
        <div style={grid2}>
          <Field label="Review Date" type="date" value={formData.reviewDate} onChange={(event) => setFormData((previous) => ({ ...previous, reviewDate: event.target.value }))} required note="Displays as Last Review Date on Training Summary" />
          <Field label="Next Review Date" type="date" value={formData.nextReviewDate} onChange={(event) => setFormData((previous) => ({ ...previous, nextReviewDate: event.target.value }))} required note="Displays as Next Review Date on Training Summary" />
        </div>
        <Divider />
        <p style={{...sec,fontSize:"14px"}}>Overall Competency Evaluation</p>
        {COMPETENCY_EVALUATION_QUESTIONS.map(q=>(
          <div key={q} style={{marginBottom:"10px"}}>
            <div style={{fontSize:"13px",color:"var(--color-text-primary)",marginBottom:"5px"}}>{q}</div>
            <div style={{display:"flex",gap:"16px"}}>
              {CONF_OPTS.map(o=>(
                <label key={o} style={{display:"flex",alignItems:"center",gap:"5px",fontSize:"13px",cursor:"pointer"}}>
                  <input
                    type="radio"
                    name={q}
                    checked={formData.overallEvaluations[q] === o}
                    onChange={() => setFormData((previous) => ({
                      ...previous,
                      overallEvaluations: {
                        ...previous.overallEvaluations,
                        [q]: o
                      }
                    }))}
                    style={{accentColor:T}}
                  /> {o}
                </label>
              ))}
            </div>
          </div>
        ))}
        <Divider />
        <div style={{background:"var(--color-background-secondary)",borderRadius:"var(--border-radius-md)",padding:"10px 12px",marginBottom:"10px",textAlign:"left"}}>
          <p style={{fontWeight:"500",fontSize:"13px",color:"var(--color-text-primary)",margin:"0 0 6px"}}>Assessor Acknowledgement</p>
          <label style={{...checkboxRow,fontSize:"13px",cursor:"pointer"}}>
            <input
              type="checkbox"
              checked={formData.assessorAcknowledgement}
              onChange={(event) => setFormData((previous) => ({ ...previous, assessorAcknowledgement: event.target.checked }))}
              style={checkboxInput}
            />
            <span style={checkboxText}>I acknowledge the competency assessment is accurate to the best of my abilities to assess the employee's abilities.</span>
          </label>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px"}}>
          <p style={{...sec,fontSize:"14px",margin:0}}>Corrective Actions</p>
          <button onClick={()=>setActions(a=>[...a,{id:Date.now(),desc:"",assignedTo:"",targetDate:""}])}
            style={btn(T,"#fff",false)}>+ Add Action</button>
        </div>
        {actions.map((ca,i)=>(
          <div key={ca.id} style={{background:"var(--color-background-secondary)",borderRadius:"var(--border-radius-md)",padding:"10px",marginBottom:"8px",border:"0.5px solid var(--color-border-tertiary)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}>
              <span style={{fontWeight:"500",fontSize:"13px",color:T}}>Corrective Action {i+1}</span>
              {actions.length>1 && <button onClick={()=>setActions(a=>a.filter(x=>x.id!==ca.id))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:"14px"}}>×</button>}
            </div>
            <div style={{...grid3}}>
              <div style={{gridColumn:"1/3"}}><Field label="Description" type="textarea" value={ca.desc} onChange={(event) => setActions((items) => items.map((item) => item.id === ca.id ? { ...item, desc: event.target.value } : item))} placeholder="Describe the corrective action..." rows={2} /></div>
              <Field label="Assigned To" type="select" value={ca.assignedTo} onChange={(event) => setActions((items) => items.map((item) => item.id === ca.id ? { ...item, assignedTo: event.target.value } : item))} options={employees.map(getEmployeeName).filter(Boolean)} placeholder="Select person" />
            </div>
            <div style={grid2}>
              <Field label="Target Date" type="date" value={ca.targetDate} onChange={(event) => setActions((items) => items.map((item) => item.id === ca.id ? { ...item, targetDate: event.target.value } : item))} />
            </div>
          </div>
        ))}
        <Field label="Supporting Document" type="file" onChange={(event) => setFormData((previous) => ({ ...previous, supportingDocumentName: event.target.files?.[0]?.name || '', supportingDocumentFile: event.target.files?.[0] || null }))} note="Attach the completed paper assessment if applicable" />
        <FormActions draft={false} submit="Submit for Final Approval" busy={isSaving} autosaveStatus={competencyAutosaveStatus} onSubmit={() => submitCompetencyRecord('SUBMITTED')} />
      </Section>
    </div>
  );
}

// ─── SCREEN 4: Employee Orientation ─────────────────────────────────────────
const ORIENT_QUIZ = [
  {q:"The three basic rights are the right to be informed, the right to refuse and the right to participate.",a:"Yes"},
  {q:"Job Hazard Assessment is a way to keep you safe on the job.",a:"Yes"},
  {q:"Tools should be inspected only according to the maintenance schedule.",a:"No"},
  {q:"Reporting First Aid is required, even if you only need a bandage.",a:"Yes"},
  {q:"If you're unsure how to do a job, you should figure it out yourself.",a:"No"},
  {q:"It is okay to use damaged equipment or tools if they still work.",a:"No"},
  {q:"If you feel a job may be unsafe, you have the right to refuse it until hazards are controlled.",a:"Yes"},
];
const parseCsvRows = (csvText = '') => {
  const rows = [];
  let row = [];
  let value = '';
  let inQuotes = false;

  for (let index = 0; index < csvText.length; index += 1) {
    const char = csvText[index];
    const nextChar = csvText[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        value += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      row.push(value.trim());
      value = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }
      row.push(value.trim());
      if (row.some(Boolean)) {
        rows.push(row);
      }
      row = [];
      value = '';
    } else {
      value += char;
    }
  }

  row.push(value.trim());
  if (row.some(Boolean)) {
    rows.push(row);
  }

  return rows;
};
const parseWhmisQuizQuestions = (csvText = '') => parseCsvRows(csvText)
  .map(([question, answer, , correction]) => {
    const normalizedAnswer = String(answer || '').trim().toLowerCase();
    if (!question || !['yes', 'no'].includes(normalizedAnswer)) {
      return null;
    }

    const correctAnswer = normalizedAnswer === 'yes' ? 'Yes' : 'No';
    return {
      q: question,
      a: correctAnswer,
      correction: correction || `Incorrect — correct answer is ${correctAnswer}. Please review the WHMIS training before continuing.`
    };
  })
  .filter(Boolean);
const ORIENTATION_SUPERVISOR_CHECKLIST_ITEMS = [
  ["Employee Handbook/HR Policies","M"],["Payroll Information","M"],["Introduction to Staff/Committee Members","M"],
  ["Working with the Public","M"],["Working Alone/Shifts/After Hours","M"],["Emergency Lighting","M"],
  ["Emergency Response Plan & Evacuation","M"],["Muster Point/Evacuation Routes","M"],["Security/Alarm System","M"],
  ["Safety Bulletin Board","M"],["Formal and Site-specific Hazard Assessments","M"],["OHS Legislation & H&S Manual","M"],
  ["WHMIS Binder & Safety Data Sheets","M"],["Washrooms","M"],["Lunchroom","M"],["First Aid Kits","M"],
  ["Fire Extinguishers","M"],["AED","M"],
  ["Hoisting/Lifting","O"],["Work Cell Phones","O"],["Commercial Vehicle Operators SWP","O"],
  ["Driver's Abstract","O"],["Criminal Record Check","O"]
];
function AddOrientationRecord({ initialView = 'employee', initialRecordId = '' }) {
  const [empType,setEmpType] = useState("New Hire");
  const [watched1,setWatched1] = useState(false);
  const [watched2,setWatched2] = useState(false);
  const [watched3,setWatched3] = useState(false);
  const [answers,setAnswers] = useState({});
  const [whmisAnswers,setWhmisAnswers] = useState({});
  const [whmisQuiz,setWhmisQuiz] = useState([]);
  const [isLoadingWhmisQuiz,setIsLoadingWhmisQuiz] = useState(false);
  const [whmisQuizError,setWhmisQuizError] = useState('');
  const [bootstrap, setBootstrap] = useState({ currentUser: null });
  const [isSaving, setIsSaving] = useState(false);
  const [orientationView, setOrientationView] = useState(initialView === 'supervisor' ? 'supervisor' : 'employee');
  const [orientationNotes, setOrientationNotes] = useState('');
  const [orientationAcknowledged, setOrientationAcknowledged] = useState(false);
  const [orientationQueue, setOrientationQueue] = useState([]);
  const [selectedOrientationRecord, setSelectedOrientationRecord] = useState(null);
  const [isLoadingQueue, setIsLoadingQueue] = useState(false);
  const [supervisorChecklist, setSupervisorChecklist] = useState({});
  const transferred = empType==="Transferred";
  const currentUserRole = String(
    bootstrap.currentUser?.role
    || bootstrap.currentUser?.ROLE
    || bootstrap.currentUser?.title
    || bootstrap.currentUser?.TITLE
    || ''
  ).toLowerCase();
  const currentUserDatabaseRole = String(bootstrap.currentUser?.role || bootstrap.currentUser?.ROLE || '').trim().toLowerCase();
  const canAccessSupervisorView = /supervisor|manager/.test(currentUserRole) || currentUserDatabaseRole === 'admin';
  const requestedOrientationRecordId = String(initialRecordId || '').trim();

  const restoreOrientationAutosave = useCallback((draft) => {
    if (!draft || typeof draft !== 'object') return;
    const objectOrEmpty = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    if (typeof draft.empType === 'string' && draft.empType) setEmpType(draft.empType);
    if (typeof draft.watched1 === 'boolean') setWatched1(draft.watched1);
    if (typeof draft.watched2 === 'boolean') setWatched2(draft.watched2);
    if (typeof draft.watched3 === 'boolean') setWatched3(draft.watched3);
    setAnswers(objectOrEmpty(draft.answers));
    setWhmisAnswers(objectOrEmpty(draft.whmisAnswers));
    setOrientationNotes(typeof draft.orientationNotes === 'string' ? draft.orientationNotes : '');
    if (typeof draft.orientationAcknowledged === 'boolean') setOrientationAcknowledged(draft.orientationAcknowledged);
    setSupervisorChecklist(objectOrEmpty(draft.supervisorChecklist));
  }, []);

  const orientationAutosaveSnapshot = useCallback(() => ({
    empType,
    watched1,
    watched2,
    watched3,
    answers,
    whmisAnswers,
    orientationNotes,
    orientationAcknowledged,
    supervisorChecklist
  }), [empType, watched1, watched2, watched3, answers, whmisAnswers, orientationNotes, orientationAcknowledged, supervisorChecklist]);
  const orientationAutosaveStatus = useTrainingAutosave('orientation', orientationAutosaveSnapshot, restoreOrientationAutosave);

  const loadOrientationQueue = useCallback(async () => {
    setIsLoadingQueue(true);
    try {
      const response = await API.get('/api/training/summary', {
        params: {
          status: 'SUBMITTED',
          categoryCode: 'employee_orientation'
        }
      });

      const records = [...(response.data?.records || [])].sort((left, right) => {
        const leftTime = new Date(left.submittedAt || left.completionDate || 0).getTime();
        const rightTime = new Date(right.submittedAt || right.completionDate || 0).getTime();
        return rightTime - leftTime;
      });
      setOrientationQueue(records);
      setSelectedOrientationRecord((currentSelection) => {
        if (requestedOrientationRecordId && records.some((record) => String(record.id) === requestedOrientationRecordId)) {
          return records.find((record) => String(record.id) === requestedOrientationRecordId) || null;
        }
        if (currentSelection && records.some((record) => String(record.id) === String(currentSelection.id))) {
          return records.find((record) => String(record.id) === String(currentSelection.id)) || null;
        }
        return records[0] || null;
      });
    } catch (error) {
      setOrientationQueue([]);
      setSelectedOrientationRecord(null);
    } finally {
      setIsLoadingQueue(false);
    }
  }, [requestedOrientationRecordId]);

  useEffect(() => {
    let cancelled = false;

    const loadBootstrap = async () => {
      try {
        const response = await API.get('/api/training/bootstrap');
        if (!cancelled && response.data?.success) {
          setBootstrap({ currentUser: response.data.currentUser || null });
        }
      } catch (error) {
        if (!cancelled) {
          setBootstrap({ currentUser: null });
        }
      }
    };

    loadBootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (orientationView === 'supervisor' && canAccessSupervisorView) {
      loadOrientationQueue();
    }
  }, [orientationView, canAccessSupervisorView, loadOrientationQueue]);

  useEffect(() => {
    if (bootstrap.currentUser && !canAccessSupervisorView && orientationView === 'supervisor') {
      setOrientationView('employee');
    }
  }, [bootstrap.currentUser, canAccessSupervisorView, orientationView]);

  useEffect(() => {
    let cancelled = false;

    const loadWhmisQuiz = async () => {
      setIsLoadingWhmisQuiz(true);
      setWhmisQuizError('');
      try {
        const response = await fetch(whmisQuizQuestionsCsv);
        if (!response.ok) {
          throw new Error('Unable to load WHMIS quiz questions.');
        }
        const csvText = await response.text();
        const parsedQuiz = parseWhmisQuizQuestions(csvText);
        if (!cancelled) {
          setWhmisQuiz(parsedQuiz);
        }
      } catch (error) {
        if (!cancelled) {
          setWhmisQuiz([]);
          setWhmisQuizError(error.message || 'Unable to load WHMIS quiz questions.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingWhmisQuiz(false);
        }
      }
    };

    loadWhmisQuiz();

    return () => {
      cancelled = true;
    };
  }, []);

  const todayValue = new Date().toISOString().slice(0, 10);
  const submitOrientationRecord = async (status) => {
    setIsSaving(true);
    try {
      const currentUserName = [bootstrap.currentUser?.firstName, bootstrap.currentUser?.lastName].filter(Boolean).join(' ')
        || bootstrap.currentUser?.displayName
        || bootstrap.currentUser?.username
        || 'Employee';
      const currentUserEmail = bootstrap.currentUser?.email || bootstrap.currentUser?.EMAIL || bootstrap.currentUser?.username || null;

      const response = await API.post('/api/training/records', {
        employeeEmail: currentUserEmail,
        employeeName: currentUserName,
        departmentName: bootstrap.currentUser?.department || bootstrap.currentUser?.departmentName || null,
        employmentType: empType,
        categoryCode: 'employee_orientation',
        categoryName: 'Employee Orientation',
        recordType: 'MANDATORY',
        summarySection: 'Mandatory Training',
        courseDescription: 'Employee Orientation',
        completionDate: todayValue,
        notes: orientationNotes,
        part1Acknowledged: orientationAcknowledged,
        part1Selections: {
          healthSafetyVideoWatched: watched1,
          whmisVideoWatched: watched2,
          harassmentVideoWatched: watched3,
          healthSafetyQuizAnswers: answers,
          whmisQuizAnswers: whmisAnswers
        },
        status,
        sourceType: 'MANUAL'
      });

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to save orientation record');
      }

      window.alert(status === 'SUBMITTED' ? 'Orientation submitted for approval.' : 'Orientation draft saved.');
    } catch (error) {
      window.alert(error?.response?.data?.error || error.message || 'Unable to save orientation.');
    } finally {
      setIsSaving(false);
    }
  };

  const approveOrientationRecord = async () => {
    if (!selectedOrientationRecord?.id) {
      window.alert('Select an employee orientation record first.');
      return;
    }

    const selectedChecklist = supervisorChecklist[String(selectedOrientationRecord.id)] || {};
    const hasUncheckedMandatoryItems = ORIENTATION_SUPERVISOR_CHECKLIST_ITEMS
      .filter(([, type]) => type === 'M')
      .some(([item]) => !selectedChecklist[item]);

    if (hasUncheckedMandatoryItems) {
      window.alert('Please review the mandatory orientation points before approving this orientation.');
      return;
    }

    setIsSaving(true);
    try {
      const response = await API.post(`/api/training/records/${selectedOrientationRecord.id}/status`, {
        status: 'APPROVED'
      });

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to approve orientation record');
      }

      window.alert('Orientation approved and completed.');
      await loadOrientationQueue();
    } catch (error) {
      window.alert(error?.response?.data?.error || error.message || 'Unable to approve orientation.');
    } finally {
      setIsSaving(false);
    }
  };
  const selectedSupervisorChecklistKey = String(selectedOrientationRecord?.id || 'unselected');
  const selectedSupervisorChecklist = supervisorChecklist[selectedSupervisorChecklistKey] || {};
  return (
    <div>
      <div style={{background:"#eff6ff",border:"0.5px solid #bfdbfe",borderRadius:"var(--border-radius-md)",padding:"10px 14px",marginBottom:"12px",fontSize:"13px",color:"#1d4ed8"}}>
        <strong>Workflow note:</strong> Employee completes Part 1, adds notes if needed, and submits to the supervisor. The supervisor then reviews a live queue of submitted acknowledgements and completes Part 2 for each employee.
      </div>
      <div style={{display:"flex",gap:"8px",marginBottom:"12px",flexWrap:"wrap"}}>
        <button type="button" onClick={() => setOrientationView('employee')} style={btn(orientationView === 'employee' ? T : '#fff', orientationView === 'employee' ? '#fff' : 'var(--color-text-primary)', orientationView !== 'employee')}>
          Employee View
        </button>
        {canAccessSupervisorView && (
          <button type="button" onClick={() => setOrientationView('supervisor')} style={btn(orientationView === 'supervisor' ? T : '#fff', orientationView === 'supervisor' ? '#fff' : 'var(--color-text-primary)', orientationView !== 'supervisor')}>
            Supervisor View
          </button>
        )}
      </div>

      {!canAccessSupervisorView && (
        <div style={{background:"#f8fafc",border:"0.5px solid var(--color-border-tertiary)",borderRadius:"var(--border-radius-md)",padding:"10px 12px",marginBottom:"12px",fontSize:"12px",color:GR}}>
          Supervisor View is available to supervisors and managers only.
        </div>
      )}

      {orientationView === 'employee' && (
      <Section title="Part 1 — Employee Orientation" collapsible={false}>
        <div style={grid2}>
          <Field label="Date" type="date" required />
          <div>
            <label style={lbl}>Employment Type <span style={{color:"#ef4444"}}>*</span></label>
            <select style={sel} value={empType} onChange={e=>setEmpType(e.target.value)}>
              {["New Hire","Transferred","Seasonal","Volunteer/Casual"].map(o=><option key={o}>{o}</option>)}
            </select>
            {transferred && <p style={{fontSize:"11px",color:"#b45309",margin:"4px 0 0"}}>Quizzes and videos are not required for Transferred employees — those sections will be greyed out.</p>}
          </div>
        </div>
        <Divider />

        {/* H&S Video */}
        <div style={{background: transferred?"var(--color-background-secondary)":"var(--color-background-primary)",borderRadius:"var(--border-radius-md)",border:"0.5px solid var(--color-border-tertiary)",padding:"12px",marginBottom:"10px",opacity: transferred?0.5:1,textAlign:"left"}}>
          <div style={{fontWeight:"500",fontSize:"13px",marginBottom:"8px",color:"var(--color-text-primary)"}}>Health & Safety Program Video</div>
          <video controls preload="metadata" src={orientationVideoSources.healthSafetyProgramVideo} style={{...orientationVideoStyle,pointerEvents: transferred?"none":"auto"}}>
            Your browser does not support the Health & Safety Program orientation video.
          </video>
          <label style={{...checkboxRow,fontSize:"13px",cursor: transferred?"not-allowed":"pointer"}}>
            <input type="checkbox" checked={watched1} style={checkboxInput} disabled={transferred} onChange={e=>setWatched1(e.target.checked)} />
            <span style={checkboxText}>I have watched the Health & Safety Program video</span>
          </label>
          {watched1 && !transferred && (
            <div style={{marginTop:"10px"}}>
              <p style={{fontWeight:"500",fontSize:"13px",color:"var(--color-text-primary)",marginBottom:"8px"}}>Health & Safety Quiz — answer Yes or No</p>
              {ORIENT_QUIZ.map((item,i)=>{
                const ans = answers[i];
                const correct = ans===item.a;
                return (
                  <div key={i} style={{marginBottom:"8px",padding:"8px",borderRadius:"var(--border-radius-md)",
                    background: ans===undefined?"var(--color-background-secondary)": correct?TB:"#fef2f2",
                    border:`0.5px solid ${ans===undefined?"var(--color-border-tertiary)":correct?T:"#fca5a5"}`}}>
                    <p style={{fontSize:"12px",color:"var(--color-text-primary)",margin:"0 0 5px"}}>{item.q}</p>
                    <div style={{display:"flex",gap:"12px"}}>
                      {["Yes","No"].map(v=>(
                        <label key={v} style={{display:"flex",alignItems:"center",gap:"4px",fontSize:"12px",cursor:"pointer"}}>
                          <input type="radio" name={`q${i}`} checked={answers[i] === v} style={{accentColor:T}} onChange={()=>setAnswers(a=>({...a,[i]:v}))} /> {v}
                        </label>
                      ))}
                    </div>
                    {ans && !correct && <p style={{fontSize:"11px",color:"#dc2626",margin:"5px 0 0"}}>Incorrect — correct answer is <strong>{item.a}</strong>. Please re-read the related policy before continuing.</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* WHMIS Video */}
        <div style={{background: transferred?"var(--color-background-secondary)":"var(--color-background-primary)",borderRadius:"var(--border-radius-md)",border:"0.5px solid var(--color-border-tertiary)",padding:"12px",marginBottom:"10px",opacity: transferred?0.5:1,textAlign:"left"}}>
          <div style={{fontWeight:"500",fontSize:"13px",marginBottom:"8px",color:"var(--color-text-primary)"}}>WHMIS 2015 Video</div>
          <video controls preload="metadata" src={orientationVideoSources.whmisTrainingVideo} style={{...orientationVideoStyle,pointerEvents: transferred?"none":"auto"}}>
            Your browser does not support the WHMIS 2015 orientation video.
          </video>
          <label style={{...checkboxRow,fontSize:"13px",cursor: transferred?"not-allowed":"pointer"}}>
            <input type="checkbox" checked={watched2} style={checkboxInput} disabled={transferred} onChange={e=>setWatched2(e.target.checked)} />
            <span style={checkboxText}>I have watched the WHMIS 2015 video</span>
          </label>
          {watched2 && !transferred && (
            <div style={{marginTop:"10px"}}>
              <p style={{fontWeight:"500",fontSize:"13px",color:"var(--color-text-primary)",marginBottom:"8px"}}>WHMIS Quiz — answer Yes or No</p>
              {isLoadingWhmisQuiz && <p style={{fontSize:"12px",color:GR,margin:"8px 0 0",fontStyle:"italic"}}>Loading WHMIS quiz questions...</p>}
              {whmisQuizError && <p style={{fontSize:"12px",color:"#dc2626",margin:"8px 0 0"}}>{whmisQuizError}</p>}
              {!isLoadingWhmisQuiz && !whmisQuizError && whmisQuiz.map((item,i)=>{
                const ans = whmisAnswers[i];
                const correct = ans===item.a;
                return (
                  <div key={i} style={{marginBottom:"8px",padding:"8px",borderRadius:"var(--border-radius-md)",
                    background: ans===undefined?"var(--color-background-secondary)": correct?TB:"#fef2f2",
                    border:`0.5px solid ${ans===undefined?"var(--color-border-tertiary)":correct?T:"#fca5a5"}`}}>
                    <p style={{fontSize:"12px",color:"var(--color-text-primary)",margin:"0 0 5px"}}>{item.q}</p>
                    <div style={{display:"flex",gap:"12px"}}>
                      {["Yes","No"].map(v=>(
                        <label key={v} style={{display:"flex",alignItems:"center",gap:"4px",fontSize:"12px",cursor:"pointer"}}>
                          <input type="radio" name={`whmis-q${i}`} checked={whmisAnswers[i] === v} style={{accentColor:T}} onChange={()=>setWhmisAnswers(a=>({...a,[i]:v}))} /> {v}
                        </label>
                      ))}
                    </div>
                    {ans && !correct && <p style={{fontSize:"11px",color:"#dc2626",margin:"5px 0 0"}}>{item.correction}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Harassment Video placeholder */}
        <div style={{background: transferred?"var(--color-background-secondary)":"var(--color-background-primary)",borderRadius:"var(--border-radius-md)",border:"0.5px solid var(--color-border-tertiary)",padding:"12px",marginBottom:"10px",opacity: transferred?0.5:1,textAlign:"left"}}>
          <div style={{fontWeight:"500",fontSize:"13px",marginBottom:"6px",color:"var(--color-text-primary)"}}>Harassment & Violence Policy and Procedures Video</div>
          <video controls preload="metadata" src={orientationVideoSources.harassmentViolenceVideo} style={{...orientationVideoStyle,marginBottom:"8px",pointerEvents: transferred?"none":"auto"}}>
            Your browser does not support the Harassment & Violence orientation video.
          </video>
          <label style={{...checkboxRow,fontSize:"13px",cursor: transferred?"not-allowed":"pointer"}}>
            <input type="checkbox" checked={watched3} style={checkboxInput} disabled={transferred} onChange={e=>setWatched3(e.target.checked)} />
            <span style={checkboxText}>I have watched the Harassment & Violence video</span>
          </label>
        </div>

        <Field
          label="Notes or Follow-up Instructions to Supervisor"
          type="textarea"
          value={orientationNotes}
          onChange={(event) => setOrientationNotes(event.target.value)}
          placeholder="Free-form notes..."
        />
        <div style={{background:"var(--color-background-secondary)",borderRadius:"var(--border-radius-md)",padding:"10px 12px",margin:"10px 0",textAlign:"left"}}>
          <label style={{...checkboxRow,fontSize:"13px",cursor:"pointer"}}>
            <input type="checkbox" checked={orientationAcknowledged} onChange={(event) => setOrientationAcknowledged(event.target.checked)} style={checkboxInput} />
            <span style={checkboxText}>I acknowledge that I have completed the orientation videos and quiz questions accurately.</span>
          </label>
        </div>
        <FormActions draft={false} submit="Submit to Supervisor" busy={isSaving} autosaveStatus={orientationAutosaveStatus} onSubmit={() => submitOrientationRecord('SUBMITTED')} />
      </Section>
      )}

      {orientationView === 'supervisor' && (
      <Section title="Part 2 — Supervisor Queue & Checklist" collapsible={false}>
        <p style={{fontSize:"12px",color:GR,marginBottom:"10px"}}>This is the supervisor queue for employee orientation. Select a submitted acknowledgement to review the details, then complete Part 2 for that person.</p>
        <div style={{background:"var(--color-background-secondary)",borderRadius:"var(--border-radius-md)",padding:"10px 12px",marginBottom:"12px",fontSize:"12px",color:"var(--color-text-primary)"}}>
          <strong>Queue note:</strong> The queue shows employees who have submitted Part 1, with submitted date and hire type.
        </div>

        <div style={{marginBottom:"10px",fontSize:"12px",color:GR}}>
          {isLoadingQueue
            ? 'Loading submitted orientations...'
            : `${orientationQueue.length} submitted orientation acknowledgement${orientationQueue.length === 1 ? '' : 's'} in queue.`}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))",gap:"10px",marginBottom:"12px"}}>
          {isLoadingQueue && (
            <div style={{padding:"12px",fontSize:"12px",color:GR,gridColumn:"1 / -1"}}>Loading submitted orientations...</div>
          )}
          {!isLoadingQueue && orientationQueue.length === 0 && (
            <div style={{padding:"12px",fontSize:"12px",color:GR,gridColumn:"1 / -1"}}>No submitted orientation acknowledgements waiting for review.</div>
          )}
          {!isLoadingQueue && orientationQueue.map((record) => {
            const isSelected = String(selectedOrientationRecord?.id || '') === String(record.id || '');
            const submittedLabel = record.submittedAt ? new Date(record.submittedAt).toLocaleString() : (record.completionDate || 'Pending');
            return (
              <button
                key={record.id}
                type="button"
                onClick={() => setSelectedOrientationRecord(record)}
                style={{
                  textAlign:"left",
                  border:`1px solid ${isSelected ? T : 'var(--color-border-tertiary)'}`,
                  borderRadius:"var(--border-radius-md)",
                  background:isSelected ? TB : "var(--color-background-primary)",
                  color:"var(--color-text-primary)",
                  padding:"12px",
                  cursor:"pointer",
                  boxShadow:isSelected ? '0 0 0 1px rgba(31, 169, 122, 0.12)' : 'none'
                }}
              >
                <div style={{display:"flex",justifyContent:"space-between",gap:"8px",alignItems:"flex-start",marginBottom:"8px"}}>
                  <div>
                    <div style={{fontSize:"13px",fontWeight:"600",color:"var(--color-text-primary)",lineHeight:"1.4"}}>{record.employeeEmail || record.employeeName || 'Unknown employee'}</div>
                    <div style={{fontSize:"11px",color:GR,marginTop:"2px"}}>Department: {record.departmentName || 'N/A'}</div>
                  </div>
                  {isSelected && <Badge c="teal" t="Selected" />}
                </div>
                <div style={{fontSize:"11px",color:GR,lineHeight:"1.55"}}>
                  <div>Submitted: {submittedLabel}</div>
                  <div>Hire type: {record.employmentType || 'N/A'}</div>
                  <div>Status: {record.status || 'SUBMITTED'}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{background:"var(--color-background-primary)",borderRadius:"var(--border-radius-md)",border:"0.5px solid var(--color-border-tertiary)",padding:"14px",textAlign:"left"}}>
          <div style={{background:"var(--color-background-secondary)",borderRadius:"var(--border-radius-md)",padding:"10px 12px",marginBottom:"10px",fontSize:"12px",color:"var(--color-text-primary)"}}>
            {selectedOrientationRecord ? (
              <>
                <strong>{selectedOrientationRecord.employeeEmail || selectedOrientationRecord.employeeName || 'Selected employee'}</strong> submitted Part 1 on {selectedOrientationRecord.submittedAt ? new Date(selectedOrientationRecord.submittedAt).toLocaleString() : 'an unknown date'}.
                <div style={{marginTop:"4px"}}>Hire type: {selectedOrientationRecord.employmentType || 'N/A'} | Department: {selectedOrientationRecord.departmentName || 'N/A'}</div>
                {selectedOrientationRecord.notes && <div style={{marginTop:"4px"}}>Employee notes: {selectedOrientationRecord.notes}</div>}
                {selectedOrientationRecord.approvedBy && <div style={{marginTop:"4px"}}>Assessed by {selectedOrientationRecord.approvedBy}{selectedOrientationRecord.approvedAt ? ` on ${new Date(selectedOrientationRecord.approvedAt).toLocaleString()}` : ''}</div>}
              </>
            ) : (
              'Select a queue item to open the original metadata and checklist.'
            )}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))",gap:"6px",marginBottom:"12px"}}>
            {ORIENTATION_SUPERVISOR_CHECKLIST_ITEMS.map(([item,type])=>(
              <label key={item} style={{...checkboxRow,fontSize:"12px",cursor:"pointer",padding:"4px 0"}}>
                <input
                  type="checkbox"
                  checked={!!selectedSupervisorChecklist[item]}
                  onChange={(event) => setSupervisorChecklist((previous) => ({
                    ...previous,
                    [selectedSupervisorChecklistKey]: {
                      ...(previous[selectedSupervisorChecklistKey] || {}),
                      [item]: event.target.checked
                    }
                  }))}
                  style={checkboxInput}
                />
                <span style={checkboxText}>{item} {type==="O" && <Badge c="blue" t="Optional" />}</span>
              </label>
            ))}
          </div>

          <FormActions submit="Approve & Complete Orientation" draft={false} busy={isSaving || !selectedOrientationRecord} autosaveStatus={orientationAutosaveStatus} onSubmit={approveOrientationRecord} />
        </div>
      </Section>
      )}
    </div>
  );
}

// ─── SCREEN 5: ERP Drill Report ──────────────────────────────────────────────
const ERP_CHECKLIST_ITEMS = [
  'Alarm Sounded',
  'All Personnel Accounted For?',
  'Equipment Shut Off',
  'Lights Turned Off',
  'Doors Shut'
];

function AddERPDrillReport() {
  const [bootstrap, setBootstrap] = useState({ currentUser: null, departments: [], employees: [] });
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [actions,setActions] = useState([{id:1,desc:"",assignedTo:"",targetDate:""}]);
  const [attachments, setAttachments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    reportDate: new Date().toISOString().slice(0, 10),
    nextDrillDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    drillCoordinatorName: '',
    drillCoordinatorEmail: '',
    reviewType: 'Annual Drill',
    personnelOnSite: '',
    topicsCovered: '',
    location: '',
    alarmSignal: '',
    evacuationTime: '',
    injuriesDuringEvacuation: '0',
    checklist: {},
    comments: '',
    acknowledgement: false
  });

  const restoreErpAutosave = useCallback((draft) => {
    if (!draft || typeof draft !== 'object') return;
    if (Array.isArray(draft.selectedDepartments)) {
      setSelectedDepartments(draft.selectedDepartments);
    }
    if (Array.isArray(draft.selectedParticipants)) {
      setSelectedParticipants(draft.selectedParticipants);
    }
    if (Array.isArray(draft.actions) && draft.actions.length > 0) {
      setActions(draft.actions);
    }
    if (draft.formData && typeof draft.formData === 'object') {
      setFormData((previous) => ({ ...previous, ...draft.formData }));
    }
  }, []);

  const erpAutosaveSnapshot = useCallback(() => ({
    selectedDepartments,
    selectedParticipants,
    actions,
    formData,
    attachmentNames: attachments.map((file) => file.name).filter(Boolean)
  }), [selectedDepartments, selectedParticipants, actions, formData, attachments]);
  const erpAutosaveStatus = useTrainingAutosave('erp-drill-report', erpAutosaveSnapshot, restoreErpAutosave);

  useEffect(() => {
    let cancelled = false;

    const loadBootstrap = async () => {
      try {
        const response = await API.get('/api/erpDrillReports/bootstrap', {
          params: selectedDepartments.length > 0 ? { departments: selectedDepartments.join(',') } : {}
        });

        if (!cancelled && response.data?.success) {
          const currentUser = response.data.currentUser || null;
          const departments = Array.isArray(response.data.departments) ? response.data.departments : [];
          const employees = Array.isArray(response.data.employees) ? response.data.employees : [];
          setBootstrap({ currentUser, departments, employees });

          const coordinatorName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ')
            || currentUser?.displayName
            || currentUser?.username
            || '';
          setFormData((previous) => previous.drillCoordinatorName ? previous : ({
            ...previous,
            drillCoordinatorName: coordinatorName,
            drillCoordinatorEmail: currentUser?.email || currentUser?.username || previous.drillCoordinatorEmail
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setBootstrap({ currentUser: null, departments: [], employees: [] });
        }
      }
    };

    loadBootstrap();

    return () => {
      cancelled = true;
    };
  }, [selectedDepartments]);

  useEffect(() => {
    if (bootstrap.employees.length === 0) return;
    setSelectedParticipants((current) => {
      const availableNames = new Set(bootstrap.employees.map((employee) => getEmployeeName(employee)).filter(Boolean));
      return current.filter((participant) => availableNames.has(participant));
    });
  }, [bootstrap.employees]);

  const departmentOptions = (bootstrap.departments.length > 0 ? bootstrap.departments : ERP_FALLBACK_DEPARTMENTS)
    .map((department) => department.name || department.NAME || department)
    .filter(Boolean);
  const availableEmployees = (bootstrap.employees.length > 0 ? bootstrap.employees : []).filter((employee) => {
    if (selectedDepartments.length === 0) {
      return true;
    }

    const employeeDepartment = String(employee.department || employee.departmentName || '').trim();
    return selectedDepartments.includes(employeeDepartment);
  });
  const selectedEmployeeObjects = availableEmployees.filter((employee) => selectedParticipants.includes(getEmployeeName(employee)));
  const drillCoordinatorOptions = availableEmployees.length > 0 ? availableEmployees.map(getEmployeeName).filter(Boolean) : [
    'Doreen Von Gradulewski',
    'Tom Bradley',
    'Sandra Liu'
  ];

  const submitERPDrillReport = async (status) => {
    setIsSaving(true);
    try {
      if (status === 'SUBMITTED' && !formData.acknowledgement) {
        throw new Error('Please acknowledge the ERP drill report before submitting it.');
      }

      const reportPayload = new FormData();
      reportPayload.append('status', status);
      reportPayload.append('reportDate', formData.reportDate || '');
      reportPayload.append('nextDrillDate', formData.nextDrillDate || '');
      reportPayload.append('departments', JSON.stringify(selectedDepartments));
      reportPayload.append('participants', JSON.stringify(selectedEmployeeObjects.map((employee) => ({
        name: getEmployeeName(employee),
        email: employee.email || employee.username || '',
        department: employee.department || employee.departmentName || ''
      }))));
      reportPayload.append('drillCoordinatorName', formData.drillCoordinatorName || '');
      reportPayload.append('drillCoordinatorEmail', formData.drillCoordinatorEmail || '');
      reportPayload.append('reviewType', formData.reviewType || '');
      reportPayload.append('personnelOnSite', formData.personnelOnSite || '0');
      reportPayload.append('topicsCovered', formData.topicsCovered || '');
      reportPayload.append('location', formData.location || '');
      reportPayload.append('alarmSignal', formData.alarmSignal || '');
      reportPayload.append('evacuationTime', formData.evacuationTime || '');
      reportPayload.append('injuriesDuringEvacuation', formData.injuriesDuringEvacuation || '0');
      reportPayload.append('checklist', JSON.stringify(ERP_CHECKLIST_ITEMS.map((label) => ({
        label,
        value: formData.checklist[label] || 'N/A'
      }))));
      reportPayload.append('comments', formData.comments || '');
      reportPayload.append('acknowledgement', String(!!formData.acknowledgement));
      reportPayload.append('createdByName', bootstrap.currentUser?.displayName || [bootstrap.currentUser?.firstName, bootstrap.currentUser?.lastName].filter(Boolean).join(' ') || bootstrap.currentUser?.username || '');
      reportPayload.append('createdByEmail', bootstrap.currentUser?.email || bootstrap.currentUser?.username || '');
      reportPayload.append('correctiveActions', JSON.stringify(actions.filter((action) => String(action.desc || '').trim()).map((action) => {
        const assignedEmployee = availableEmployees.find((employee) => getEmployeeName(employee) === action.assignedTo) || null;
        return {
          description: action.desc,
          assignedToName: action.assignedTo,
          assignedToEmail: assignedEmployee?.email || '',
          targetDate: action.targetDate
        };
      })));
      attachments.forEach((file) => {
        reportPayload.append('attachments', file);
      });

      const response = await API.post('/api/erpDrillReports/reports', reportPayload);
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to save drill report');
      }
      window.alert(status === 'SUBMITTED' ? 'Emergency response drill submitted and acknowledgement emails sent.' : 'Emergency response drill draft saved.');
    } catch (error) {
      window.alert(error?.response?.data?.error || error.message || 'Unable to save drill report.');
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <div>
      <div style={{background:"#eff6ff",border:"0.5px solid #bfdbfe",borderRadius:"var(--border-radius-md)",padding:"10px 14px",marginBottom:"12px",fontSize:"13px",color:"#1d4ed8"}}>
        <strong>Workflow note:</strong> Submitted drill reports are bundled with attachments and emailed to all participants, the form creator, and the drill coordinator for acknowledgement.
      </div>
      <Section title="Emergency Response Drill Report" collapsible={false}>
        <div style={grid2}>
          <Field label="Date of Review" type="date" value={formData.reportDate} onChange={(event) => setFormData((previous) => ({ ...previous, reportDate: event.target.value }))} required note="Displays as Completion Date on Training Summary" />
          <Field label="Next Drill Date" type="date" value={formData.nextDrillDate} onChange={(event) => setFormData((previous) => ({ ...previous, nextDrillDate: event.target.value }))} required note="Auto-populated to 12 months; cannot be set later than 12 months" />
        </div>
        <div style={{...grid2,marginTop:"10px"}}>
          <MultiSelectDropdown
            label="Department(s)"
            options={departmentOptions}
            selected={selectedDepartments}
            onToggle={(department) => setSelectedDepartments((current) => current.includes(department)
              ? current.filter((item) => item !== department)
              : [...current, department])}
            placeholder="Select one or more departments"
          />
          <Field label="Drill Coordinator" type="select" value={formData.drillCoordinatorName} onChange={(event) => setFormData((previous) => ({ ...previous, drillCoordinatorName: event.target.value }))} options={drillCoordinatorOptions} placeholder="Defaults to logged-in user" />
        </div>
        <div style={{marginTop:"10px"}}>
          <MultiSelectDropdown
            label="Employees Participating"
            options={availableEmployees.map(getEmployeeName).filter(Boolean)}
            selected={selectedParticipants}
            onToggle={(employeeName) => setSelectedParticipants((current) => current.includes(employeeName)
              ? current.filter((item) => item !== employeeName)
              : [...current, employeeName])}
            placeholder={selectedDepartments.length === 0 ? 'Select a department first' : 'Select one or more employees'}
          />
          <p style={{fontSize:"11px",color:GR,margin:"3px 0 0",lineHeight:"1.4"}}>ERP Drill report acknowledgements are emailed to each selected participant.</p>
        </div>
        <Divider />
        <div style={grid2}>
          <Field label="Type of Review" type="select" value={formData.reviewType} onChange={(event) => setFormData((previous) => ({ ...previous, reviewType: event.target.value }))} options={["Actual Emergency","Annual Drill","Table Topic Review"]} required />
          <Field label="Number of Personnel On Site" type="number" value={formData.personnelOnSite} onChange={(event) => setFormData((previous) => ({ ...previous, personnelOnSite: event.target.value }))} placeholder="e.g. 12" />
        </div>
        <div style={grid2}>
          <Field label="Emergency Topics Covered" type="textarea" value={formData.topicsCovered} onChange={(event) => setFormData((previous) => ({ ...previous, topicsCovered: event.target.value }))} placeholder="Describe the emergency topics covered..." rows={2} />
          <Field label="Location of Drill / Event" value={formData.location} onChange={(event) => setFormData((previous) => ({ ...previous, location: event.target.value }))} placeholder="Free-form location description" />
        </div>
        <div style={{...grid3,marginTop:"10px"}}>
          <Field label="Type of Alarm / Signal Used" value={formData.alarmSignal} onChange={(event) => setFormData((previous) => ({ ...previous, alarmSignal: event.target.value }))} placeholder="e.g. Fire alarm, verbal" />
          <Field label="Time Taken to Evacuate" value={formData.evacuationTime} onChange={(event) => setFormData((previous) => ({ ...previous, evacuationTime: event.target.value }))} placeholder="e.g. 3 min 20 sec" />
          <Field label="Injuries During Evacuation" type="number" value={formData.injuriesDuringEvacuation} onChange={(event) => setFormData((previous) => ({ ...previous, injuriesDuringEvacuation: event.target.value }))} placeholder="0" note="If > 0, complete an Incident Report" />
        </div>
        <Divider />
        <div style={grid3}>
          {ERP_CHECKLIST_ITEMS.map((label) => (
            <Field
              key={label}
              label={label}
              type="select"
              value={formData.checklist[label] || ''}
              onChange={(event) => setFormData((previous) => ({
                ...previous,
                checklist: {
                  ...previous.checklist,
                  [label]: event.target.value
                }
              }))}
              options={["Yes","No","N/A"]}
            />
          ))}
        </div>
        <div style={{marginTop:"10px"}}>
          <Field label="Overall Comments (what went well, what needs improvement)" type="textarea" value={formData.comments} onChange={(event) => setFormData((previous) => ({ ...previous, comments: event.target.value }))} placeholder="Free-form comments..." rows={3} />
        </div>
        <Divider />
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px"}}>
          <p style={{...sec,fontSize:"14px",margin:0}}>Corrective Actions</p>
          <button type="button" onClick={()=>setActions(a=>[...a,{id:Date.now(),desc:"",assignedTo:"",targetDate:""}])}
            style={btn(T,"#fff",false)}>+ Add Action</button>
        </div>
        {actions.map((ca,i)=>(
          <div key={ca.id} style={{background:"var(--color-background-secondary)",borderRadius:"var(--border-radius-md)",padding:"10px",marginBottom:"8px",border:"0.5px solid var(--color-border-tertiary)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}>
              <span style={{fontWeight:"500",fontSize:"13px",color:T}}>Corrective Action {i+1}</span>
              {actions.length>1 && <button onClick={()=>setActions(a=>a.filter(x=>x.id!==ca.id))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:"14px"}}>×</button>}
            </div>
            <div style={{...grid3}}>
              <div style={{gridColumn:"1/3"}}><Field label="Description" type="textarea" value={ca.desc} onChange={(event) => setActions((items) => items.map((item) => item.id === ca.id ? { ...item, desc: event.target.value } : item))} placeholder="Describe the corrective action..." rows={2} /></div>
              <Field label="Assigned To" type="select" value={ca.assignedTo} onChange={(event) => setActions((items) => items.map((item) => item.id === ca.id ? { ...item, assignedTo: event.target.value } : item))} options={availableEmployees.map(getEmployeeName).filter(Boolean)} placeholder="Select person" />
            </div>
            <div style={grid2}>
              <Field label="Target Date" type="date" value={ca.targetDate} onChange={(event) => setActions((items) => items.map((item) => item.id === ca.id ? { ...item, targetDate: event.target.value } : item))} />
            </div>
          </div>
        ))}
        <div style={{marginTop:"10px"}}>
          <label style={lbl}>Attachments</label>
          <input
            type="file"
            multiple
            accept=".pdf,image/*"
            style={inp}
            onChange={(event) => setAttachments(Array.from(event.target.files || []))}
          />
          <p style={{fontSize:"11px",color:GR,margin:"3px 0 0",lineHeight:"1.4"}}>Attach photos, signed rosters or supporting documents. They will be appended to the final ERP drill PDF.</p>
        </div>
        <Divider />
        <div style={{background:"var(--color-background-secondary)",borderRadius:"var(--border-radius-md)",padding:"10px 12px",marginBottom:"6px",textAlign:"left"}}>
          <p style={{fontWeight:"500",fontSize:"13px",margin:"0 0 6px",color:"var(--color-text-primary)"}}>Acknowledgements</p>
          <p style={{fontSize:"12px",color:GR,margin:"0 0 8px"}}>All participants, the form creator, and the drill coordinator will receive an email to acknowledge this report.</p>
          <label style={{...checkboxRow,fontSize:"13px",cursor:"pointer"}}>
            <input type="checkbox" checked={formData.acknowledgement} onChange={(event) => setFormData((previous) => ({ ...previous, acknowledgement: event.target.checked }))} style={checkboxInput} />
            <span style={checkboxText}>I acknowledge this Emergency Response Drill Report is accurate and complete.</span>
          </label>
        </div>
        <FormActions draft={false} submit="Submit & Notify Participants" busy={isSaving} autosaveStatus={erpAutosaveStatus} onSubmit={() => submitERPDrillReport('SUBMITTED')} />
      </Section>
    </div>
  );
}

// ─── Main Shell ───────────────────────────────────────────────────────────────
const TABS = [
  {id:"summary",label:"Training Summary"},
  {id:"add",label:"Add Training Record"},
  {id:"competency",label:"Add Competency"},
  {id:"orientation",label:"Orientation"},
  {id:"erp",label:"ERP Drill Report"},
];

const getInitialTrainingModuleParams = () => {
  if (typeof window === 'undefined') {
    return { tab: 'summary', orientationView: 'employee', orientationRecordId: '' };
  }

  const params = new URLSearchParams(window.location.search || '');
  const requestedTab = String(params.get('tab') || '').trim().toLowerCase();
  const requestedView = String(params.get('view') || '').trim().toLowerCase();
  const requestedRecordId = String(params.get('recordId') || '').trim();
  const validTab = TABS.some((tab) => tab.id === requestedTab) ? requestedTab : 'summary';

  return {
    tab: validTab,
    orientationView: requestedView === 'supervisor' ? 'supervisor' : 'employee',
    orientationRecordId: requestedRecordId
  };
};

export default function TrainingModule() {
  const initialTrainingParams = getInitialTrainingModuleParams();
  const [tab,setTab] = useState(initialTrainingParams.tab);
  const [currentUser, setCurrentUser] = useState(null);
  const [mountedTabs, setMountedTabs] = useState(() => new Set([initialTrainingParams.tab]));

  useEffect(() => {
    setMountedTabs((current) => {
      if (current.has(tab)) return current;
      const next = new Set(current);
      next.add(tab);
      return next;
    });
  }, [tab]);

  useEffect(() => {
    let cancelled = false;

    const loadCurrentUser = async () => {
      try {
        const response = await API.get('/api/training/bootstrap');
        if (!cancelled && response.data?.success) {
          setCurrentUser(response.data.currentUser || null);
        }
      } catch (error) {
        if (!cancelled) {
          setCurrentUser(null);
        }
      }
    };

    loadCurrentUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const headerDisplayName = getUserDisplayName(currentUser) || 'Current User';
  const headerRole = currentUser?.role || currentUser?.ROLE || currentUser?.title || currentUser?.TITLE || currentUser?.department || currentUser?.DEPARTMENT || '';
  return (
    <div style={{fontFamily:"var(--font-sans)",background:"var(--color-background-tertiary)",minHeight:"100vh",paddingBottom:"40px"}}>
      <h2 className="sr-only">Employee Training Module — four sub-modules</h2>
      {/* Header */}
      <div style={{background:"var(--color-background-primary)",borderBottom:"0.5px solid var(--color-border-tertiary)",
        padding:"12px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",
        position:"sticky",top:0,zIndex:300}}>
        <div>
          <span style={{fontWeight:"500",fontSize:"15px",color:"var(--color-text-primary)"}}>Employee Training Module</span>
          <span style={{fontSize:"12px",color:GR,marginLeft:"10px"}}>Health & Safety Application</span>
        </div>
        <span style={{fontSize:"12px",color:GR}}>Logged in as: <strong>{headerDisplayName}</strong>{headerRole ? `   ${headerRole}` : ''}</span>
      </div>

      {/* Tab Navigation */}
      <div style={{background:"var(--color-background-primary)",borderBottom:"0.5px solid var(--color-border-tertiary)",
        padding:"0 16px",display:"flex",gap:"0",overflowX:"auto"}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            padding:"10px 14px",fontSize:"13px",fontWeight: tab===t.id?"500":"400",
            background:"none",border:"none",cursor:"pointer",whiteSpace:"nowrap",
            color: tab===t.id?T:GR,
            borderBottom: tab===t.id?`2px solid ${T}`:"2px solid transparent",
            transition:"color 0.15s"
          }}>{t.label}</button>
        ))}
      </div>

      {/* Screen Content */}
      <div style={{maxWidth:"860px",margin:"0 auto",padding:"12px 16px 0"}}>
        {mountedTabs.has("summary") && <div style={{display: tab === "summary" ? "block" : "none"}}><TrainingSummary /></div>}
        {mountedTabs.has("add") && <div style={{display: tab === "add" ? "block" : "none"}}><AddTrainingRecord /></div>}
        {mountedTabs.has("competency") && <div style={{display: tab === "competency" ? "block" : "none"}}><AddCompetencyRecord /></div>}
        {mountedTabs.has("orientation") && <div style={{display: tab === "orientation" ? "block" : "none"}}><AddOrientationRecord initialView={initialTrainingParams.orientationView} initialRecordId={initialTrainingParams.orientationRecordId} /></div>}
        {mountedTabs.has("erp") && <div style={{display: tab === "erp" ? "block" : "none"}}><AddERPDrillReport /></div>}
      </div>
    </div>
  );
}